// process.env safe client component
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  reconnectEdge,
  Handle,
  Position,
} from '@xyflow/react';
import type { Node, Edge, NodeProps, Connection } from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  Bot,
  Globe,
  Sparkles,
  Database,
  FileText,
  CheckCircle2,
  AlertCircle,
  RefreshCw,
  Copy,
  Check,
  Shield,
  Layers,
  Code,
  User,
  LogOut,
  ArrowRight,
  ExternalLink,
  Calendar,
  Mail,
  Phone,
  Settings,
  Plus,
  Lock,
  Sliders,
  Zap,
  Volume2,
  ShieldCheck,
  Cpu,
  Terminal,
  Radio,
  Trash2,
  Edit3,
  BookOpen,
  Send,
  MessageSquare,
  Smartphone,
  QrCode,
  ShieldAlert,
  UploadCloud,
  FolderGit2,
  X,
  Maximize2,
  SlidersHorizontal,
  ChevronRight,
  HelpCircle,
  Save,
  Users,
  Activity,
  Search,
  Mic,
  AlertTriangle,
  RotateCcw,
  Undo2,
  Redo2,
  Wand2,
  CreditCard,
  PhoneForwarded,
  ChevronLeft,
  Compass,
  LayoutTemplate,
  KeyRound,
  Unlock,
  Brain,
  BellRing
} from 'lucide-react';

import { LunaVoiceModel } from '../LunaVoiceModel';

// ═══════════════════════════════════════════════════════════════════════════
// IMPORT REAL NODE COMPONENTS (1:1 PARITY WITH MASTER ADMIN BUILDER)
// ═══════════════════════════════════════════════════════════════════════════

import LunaCoreNode from '../luna-builder/nodes/LunaCoreNode';
import InstructionNode from '../luna-builder/nodes/InstructionNode';
import KnowledgeNode from '../luna-builder/nodes/KnowledgeNode';
import PermissionNode from '../luna-builder/nodes/PermissionNode';
import SecurityFilterNode from '../luna-builder/nodes/SecurityFilterNode';
import CallGuardNode from '../luna-builder/nodes/CallGuardNode';
import ActionNode from '../luna-builder/nodes/ActionNode';
import ScraperNode from '../luna-builder/nodes/ScraperNode';
import FormGeneratorNode from '../luna-builder/nodes/FormGeneratorNode';
import VoiceTypingNode from '../luna-builder/nodes/VoiceTypingNode';
import SectionNode from '../luna-builder/nodes/SectionNode';
import EmailSmtpNode from '../luna-builder/nodes/EmailSmtpNode';
import AuthGatewayNode from '../luna-builder/nodes/AuthGatewayNode';
import AuthGuardNode from '../luna-builder/nodes/AuthGuardNode';
import MediaVaultNode from '../luna-builder/nodes/MediaVaultNode';
import WhatsAppInstanceNode from '../luna-builder/nodes/WhatsAppInstanceNode';
import DatabaseNode from '../luna-builder/nodes/DatabaseNode';
import PaymentGatewayNode from '../luna-builder/nodes/PaymentGatewayNode';
import CallTransferNode from '../luna-builder/nodes/CallTransferNode';
import LunaMemoryNode from '../luna-builder/nodes/LunaMemoryNode';
import IntentAlertNode from '../luna-builder/nodes/IntentAlertNode';
import { ProductCatalogNode } from '../luna-builder/nodes/ProductCatalogNode';
import { FollowUpNode } from '../luna-builder/nodes/FollowUpNode';
import { ReviewBoosterNode } from '../luna-builder/nodes/ReviewBoosterNode';
import SelfImproveNode from '../luna-builder/nodes/SelfImproveNode';
import WebWidgetNode from '../luna-builder/nodes/WebWidgetNode';
import SipCallNode from '../luna-builder/nodes/SipCallNode';
import { isNodeWiredToLunaCore } from '../luna-builder/pulseUtils';
import { getCanonicalNodeType, getWiringEdgeConfigs, evaluateFlowHealthDiagnostics } from '../luna-builder/lunaNodeRegistry';
import { TenantViewContext, useTenantView } from '../luna-builder/TenantViewContext';
import { useSequentialPulse, type PulseStep, type PulseEdge } from '../luna-builder/useSequentialPulse';
import { ShoppingBag, Building, MoreHorizontal } from 'lucide-react';
import { IndustryBlueprintsModal } from '../luna-builder/modals/IndustryBlueprintsModal';
import { IndustryFlowBlueprint } from '../luna-builder/flowTemplates';
import { NodeInspectorStudio } from '../luna-builder/NodeInspectorStudio';

const customNodeTypes = {
  // Instruction / Persona
  instructionNode: InstructionNode,
  instruction: InstructionNode,
  personaNode: InstructionNode,
  persona: InstructionNode,
  // Knowledge
  knowledgeNode: KnowledgeNode,
  knowledge: KnowledgeNode,
  knowledgemedia: KnowledgeNode,
  KNOWLEDGE_VAULT: KnowledgeNode,
  // Luna Memory Vault
  lunaMemoryNode: LunaMemoryNode,
  LUNA_MEMORY: LunaMemoryNode,
  MEMORY_VAULT: LunaMemoryNode,
  memoryNode: LunaMemoryNode,
  memory: LunaMemoryNode,
  // Action
  actionNode: ActionNode,
  action: ActionNode,
  ownernotifywhatsapp: ActionNode,
  leadtab: ActionNode,
  ACTION_NODE: ActionNode,
  // Permission / RBAC
  permissionNode: PermissionNode,
  permission: PermissionNode,
  rbacNode: PermissionNode,
  rbac: PermissionNode,
  // Scraper
  scraperNode: ScraperNode,
  scraper: ScraperNode,
  ai_web_scraper: ScraperNode,
  aiwebscraper: ScraperNode,
  SCRAPER_NODE: ScraperNode,
  // Luna Core Hub
  lunaCoreNode: LunaCoreNode,
  LUNA_CORE_HUB: LunaCoreNode,
  coreNode: LunaCoreNode,
  core: LunaCoreNode,
  // Security Filter
  securityFilterNode: SecurityFilterNode,
  SECURITY_FILTER: SecurityFilterNode,
  security_filter: SecurityFilterNode,
  security: SecurityFilterNode,
  // Call Guard
  callGuardNode: CallGuardNode,
  CALL_GUARD: CallGuardNode,
  call_guard: CallGuardNode,
  callGuard: CallGuardNode,
  call_guard_node: CallGuardNode,
  // Form Generator
  formGeneratorNode: FormGeneratorNode,
  FORM_GENERATOR: FormGeneratorNode,
  form_generator: FormGeneratorNode,
  // Voice Typing
  voiceTypingNode: VoiceTypingNode,
  VOICE_TYPING: VoiceTypingNode,
  voice_typing: VoiceTypingNode,
  // Section / Route
  sectionNode: SectionNode,
  SECTION_NODE: SectionNode,
  SECTION: SectionNode,
  section_node: SectionNode,
  // Email SMTP
  emailSmtpNode: EmailSmtpNode,
  EMAIL_SMTP: EmailSmtpNode,
  email_smtp: EmailSmtpNode,
  // Auth Gateway
  authGatewayNode: AuthGatewayNode,
  AUTH_GATEWAY: AuthGatewayNode,
  auth_gateway: AuthGatewayNode,
  // Auth Guard
  authGuardNode: AuthGuardNode,
  AUTH_GUARD: AuthGuardNode,
  auth_guard: AuthGuardNode,
  // Media Vault
  mediaVaultNode: MediaVaultNode,
  MEDIA_VAULT: MediaVaultNode,
  media_vault: MediaVaultNode,
  // WhatsApp Instance
  whatsappInstanceNode: WhatsAppInstanceNode,
  WHATSAPP_INSTANCE: WhatsAppInstanceNode,
  whatsapp_instance: WhatsAppInstanceNode,
  whatsapp: WhatsAppInstanceNode,
  // Database / Storage
  databaseNode: DatabaseNode,
  DATABASE_STORAGE: DatabaseNode,
  DATABASE_NODE: DatabaseNode,
  database_storage: DatabaseNode,
  database: DatabaseNode,
  // Payment Gateway
  paymentGatewayNode: PaymentGatewayNode,
  PAYMENT_GATEWAY: PaymentGatewayNode,
  payment_gateway: PaymentGatewayNode,
  // Call Transfer
  callTransferNode: CallTransferNode,
  CALL_TRANSFER: CallTransferNode,
  call_transfer: CallTransferNode,
  // Smart Intent & VIP Alerts
  intentAlertNode: IntentAlertNode,
  INTENT_ALERT_NODE: IntentAlertNode,
  intent_alert: IntentAlertNode,
  intentAlert: IntentAlertNode,
  // Product Catalog & Menu
  productCatalogNode: ProductCatalogNode,
  PRODUCT_CATALOG: ProductCatalogNode,
  product_catalog: ProductCatalogNode,
  productCatalog: ProductCatalogNode,
  catalogNode: ProductCatalogNode,
  // Follow-Up & Abandoned Recovery
  followUpNode: FollowUpNode,
  FOLLOW_UP: FollowUpNode,
  followup: FollowUpNode,
  followUp: FollowUpNode,
  FOLLOWUP: FollowUpNode,
  // Review Booster & Google Maps Rating
  reviewBoosterNode: ReviewBoosterNode,
  REVIEW_BOOSTER: ReviewBoosterNode,
  review_booster: ReviewBoosterNode,
  reviewBooster: ReviewBoosterNode,
  // Self-Improvement Loop (Phase 6)
  selfImproveNode: SelfImproveNode,
  SELF_IMPROVE: SelfImproveNode,
  self_improve: SelfImproveNode,
  selfImprove: SelfImproveNode,
  // Website Live Co-Pilot & Web Widget
  webWidgetNode: WebWidgetNode,
  WEB_WIDGET_NODE: WebWidgetNode,
  web_widget: WebWidgetNode,
  webWidget: WebWidgetNode,
  website_widget: WebWidgetNode,
  // Enterprise SIP & PBX Phone Calling (Grandstream / SLT / Dialog)
  sipCallNode: SipCallNode,
  SIP_CALL_NODE: SipCallNode,
  sip_call: SipCallNode,
  sipCall: SipCallNode,
  pbxCallNode: SipCallNode,
  PBX_CALL: SipCallNode,
  pbx_call: SipCallNode,
};

import AnimatedPulseEdge from '../luna-builder/AnimatedPulseEdge';

const customEdgeTypes = {
  default: AnimatedPulseEdge,
  animated: AnimatedPulseEdge,
};

// ═══════════════════════════════════════════════════════════════════════════
// CANONICAL PALETTE NODES (SHARED SOURCE OF TRUTH)
// ═══════════════════════════════════════════════════════════════════════════

import { MASTER_PALETTE_NODES as PALETTE_NODES, type NodePaletteItem } from '../luna-builder/paletteNodes';

// ═══════════════════════════════════════════════════════════════════════════
// MAIN DYNAMIC WORKSPACE (FULL-BLEED CANVAS & DECOUPLED SLIDE-OVER)
// ═══════════════════════════════════════════════════════════════════════════

interface TenetDynamicWorkspaceProps {
  token: string;
  tenant?: any;
  onOpenPortalData?: (nodeType?: string, nodeId?: string) => void;
  onOpenAccountModal?: () => void;
}

// ── Canonical Auto-Wire Topology Helpers (Single Source of Truth Re-exported from Registry) ──
export { getCanonicalNodeType, getWiringEdgeConfigs, evaluateFlowHealthDiagnostics };

const FlowWorkspaceInner: React.FC<TenetDynamicWorkspaceProps> = ({ token, tenant, onOpenPortalData }) => {
  const { screenToFlowPosition, fitView, getNodes, getEdges } = useReactFlow();
  const reactFlowWrapper = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [flowMeta, setFlowMeta] = useState<any>(null);

  // React Flow state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Phase 7: Live Avatar Tester & Deploy Modal States
  const [isLunaTesterOpen, setIsLunaTesterOpen] = useState<boolean>(false);
  const [showDeployModal, setShowDeployModal] = useState<boolean>(false);
  const [copiedEmbedCode, setCopiedEmbedCode] = useState<boolean>(false);
  const [copiedTenantId, setCopiedTenantId] = useState<boolean>(false);

  // Phase 9: Web Scraper & Rate Limit States
  const [scrapingNodeId, setScrapingNodeId] = useState<string | null>(null);
  const [scrapeQuota, setScrapeQuota] = useState<{ remaining: number; resetInHours: number; totalAllowed: number }>({
    remaining: 3,
    resetInHours: 24,
    totalAllowed: 3,
  });
  const [scrapedKnowledgePreview, setScrapedKnowledgePreview] = useState<string | null>(null);

  // Form & Leads state for widget data persistence
  const [formValues, setFormValues] = useState<Record<string, Record<string, any>>>({});
  const [savingAll, setSavingAll] = useState<boolean>(false);
  const [leadsList, setLeadsList] = useState<any[]>([]);
  const [leadsLoading, setLeadsLoading] = useState<boolean>(false);

  const [workspaceError, setWorkspaceError] = useState<string | null>(null);

  // Notification Toast
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }, []);

  // ── History Stack for Undo / Redo & Draft Management (Customer Safety) ──
  const historyRef = useRef<Array<{ nodes: Node[]; edges: Edge[] }>>([]);
  const historyIndexRef = useRef<number>(-1);
  const isApplyingHistoryRef = useRef<boolean>(false);
  const [canUndo, setCanUndo] = useState<boolean>(false);
  const [canRedo, setCanRedo] = useState<boolean>(false);
  const [isDraft, setIsDraft] = useState<boolean>(false);
  const publishedSnapshotRef = useRef<{ nodes: Node[]; edges: Edge[] } | null>(null);

  const updateHistoryButtons = useCallback(() => {
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(historyIndexRef.current < historyRef.current.length - 1);
  }, []);

  const pushHistory = useCallback((currentNodes: Node[], currentEdges: Edge[]) => {
    if (isApplyingHistoryRef.current) return;
    const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
    const snapshot = {
      nodes: JSON.parse(JSON.stringify(currentNodes)),
      edges: JSON.parse(JSON.stringify(currentEdges)),
    };
    newHistory.push(snapshot);
    if (newHistory.length > 40) newHistory.shift();
    historyRef.current = newHistory;
    historyIndexRef.current = newHistory.length - 1;
    updateHistoryButtons();
    setIsDraft(true);
  }, [updateHistoryButtons]);

  const handleUndo = useCallback(() => {
    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      const targetSnapshot = historyRef.current[historyIndexRef.current];
      if (targetSnapshot) {
        isApplyingHistoryRef.current = true;
        setNodes(JSON.parse(JSON.stringify(targetSnapshot.nodes)));
        setEdges(JSON.parse(JSON.stringify(targetSnapshot.edges)));
        setTimeout(() => { isApplyingHistoryRef.current = false; }, 50);
        updateHistoryButtons();
        setIsDraft(true);
        setToast({ type: 'success', message: '↩️ Undo applied' });
        setTimeout(() => setToast(null), 2500);
      }
    }
  }, [setNodes, setEdges, updateHistoryButtons]);

  const handleRedo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      const targetSnapshot = historyRef.current[historyIndexRef.current];
      if (targetSnapshot) {
        isApplyingHistoryRef.current = true;
        setNodes(JSON.parse(JSON.stringify(targetSnapshot.nodes)));
        setEdges(JSON.parse(JSON.stringify(targetSnapshot.edges)));
        setTimeout(() => { isApplyingHistoryRef.current = false; }, 50);
        updateHistoryButtons();
        setIsDraft(true);
        setToast({ type: 'success', message: '↪️ Redo applied' });
        setTimeout(() => setToast(null), 2500);
      }
    }
  }, [setNodes, setEdges, updateHistoryButtons]);

  const handleDiscardDraft = useCallback(() => {
    if (publishedSnapshotRef.current) {
      isApplyingHistoryRef.current = true;
      const restoredNodes = JSON.parse(JSON.stringify(publishedSnapshotRef.current.nodes));
      const restoredEdges = JSON.parse(JSON.stringify(publishedSnapshotRef.current.edges));
      setNodes(restoredNodes);
      setEdges(restoredEdges);
      setTimeout(() => { isApplyingHistoryRef.current = false; }, 50);
      historyRef.current = [{
        nodes: JSON.parse(JSON.stringify(restoredNodes)),
        edges: JSON.parse(JSON.stringify(restoredEdges)),
      }];
      historyIndexRef.current = 0;
      updateHistoryButtons();
      setIsDraft(false);
      setToast({ type: 'success', message: 'Draft discarded. Reverted to live published flow.' });
      setTimeout(() => setToast(null), 3000);
    }
  }, [setNodes, setEdges, updateHistoryButtons]);

  // ── Auto-Detect Any Keystroke, Word, Node or Edge Edit to Trigger Draft & Record History ──
  const prevNodesEdgesJsonRef = useRef<string>('');

  useEffect(() => {
    if (loading || isApplyingHistoryRef.current || !publishedSnapshotRef.current) return;
    const currentJson = JSON.stringify({ nodes, edges });
    if (!prevNodesEdgesJsonRef.current) {
      prevNodesEdgesJsonRef.current = currentJson;
      return;
    }

    if (currentJson !== prevNodesEdgesJsonRef.current) {
      const publishedJson = JSON.stringify(publishedSnapshotRef.current);
      const isDifferentFromPublished = currentJson !== publishedJson;
      setIsDraft(isDifferentFromPublished);

      // Debounce history snapshot creation by 350ms so continuous typing groups into undo steps cleanly
      const timeoutId = setTimeout(() => {
        if (isApplyingHistoryRef.current) return;
        const newHistory = historyRef.current.slice(0, historyIndexRef.current + 1);
        const snapshot = {
          nodes: JSON.parse(JSON.stringify(nodes)),
          edges: JSON.parse(JSON.stringify(edges)),
        };
        newHistory.push(snapshot);
        if (newHistory.length > 50) newHistory.shift();
        historyRef.current = newHistory;
        historyIndexRef.current = newHistory.length - 1;
        updateHistoryButtons();
      }, 350);

      prevNodesEdgesJsonRef.current = currentJson;
      return () => clearTimeout(timeoutId);
    }
  }, [nodes, edges, loading, updateHistoryButtons]);

  // Global Keyboard Shortcuts: Ctrl+Z (Undo) and Ctrl+Y / Ctrl+Shift+Z (Redo)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if (((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'y') || ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === 'z')) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleUndo, handleRedo]);

  // Master Admin Governance Flags & AI Auto-Flow state
  const [allowTenantAutoFlow, setAllowTenantAutoFlow] = useState<boolean>(true);
  const [allowTenantToolbox, setAllowTenantToolbox] = useState<boolean>(true);
  const [showAutoFlowModal, setShowAutoFlowModal] = useState<boolean>(false);
  const [autoFlowPrompt, setAutoFlowPrompt] = useState<string>('');
  const [autoFlowGenerating, setAutoFlowGenerating] = useState<boolean>(false);
  const [autoFlowError, setAutoFlowError] = useState<string | null>(null);
  const [autoFlowSuccess, setAutoFlowSuccess] = useState<string | null>(null);

  // ── Phase 34: Floating Glassmorphism Node Palette Drawer State ──
  const [isNodeDrawerOpen, setIsNodeDrawerOpen] = useState<boolean>(false);
  const [nodeSearchQuery, setNodeSearchQuery] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'brain' | 'knowledge' | 'actions' | 'commerce' | 'security'>('all');
  const [showBlueprintsModal, setShowBlueprintsModal] = useState<boolean>(false);

  const handleSelectBlueprint = useCallback((blueprint: IndustryFlowBlueprint) => {
    const tenantKey = tenant?.apiKey || (tenant?.id ? String(tenant.id) : '');
    const tenantIdStr = tenant?.id ? String(tenant.id) : 'wa';
    const cleanVerifyToken = tenantKey ? `kangavena_wa_${tenantKey.slice(0, 16)}` : '';
    const personalizedNodes = (blueprint.nodes || []).map((nodeItem: any) => {
      const type = (nodeItem?.type || '').toLowerCase();
      if (type.includes('whatsapp')) {
        return {
          ...nodeItem,
          data: {
            ...(nodeItem.data || {}),
            metaVerifyToken: cleanVerifyToken,
            instanceName: `tenet_${tenantIdStr}`,
          },
        };
      }
      return nodeItem;
    });

    setNodes(personalizedNodes);
    setEdges(blueprint.edges);
    setShowBlueprintsModal(false);
    showToast(`🌟 Blueprint "${blueprint.title}" deployed! Click "Save Flow" to commit.`, 'success');
  }, [allowTenantToolbox, tenant, setNodes, setEdges, showToast]);

  const filteredPaletteNodes = useMemo(() => {
    return PALETTE_NODES.filter((item) => {
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      const query = nodeSearchQuery.toLowerCase().trim();
      const matchesQuery = !query || 
        item.label.toLowerCase().includes(query) || 
        item.desc.toLowerCase().includes(query) || 
        item.tag.toLowerCase().includes(query);
      return matchesCategory && matchesQuery;
    });
  }, [nodeSearchQuery, selectedCategory]);

  const handleAddNodeToCanvas = useCallback((type: string) => {
    const newNodeId = `node-${type.toLowerCase().replace('node', '')}-${Date.now().toString(36)}`;
    let initialData: any = { isCustomizable: true };
    if (type === 'paymentGatewayNode' || type === 'PAYMENT_GATEWAY') {
      initialData = { bankName: '', branchName: '', accountName: '', accountNumber: '', paymentLinkUrl: '', isCustomizable: true };
    } else if (type === 'callTransferNode' || type === 'CALL_TRANSFER') {
      initialData = { transferPhoneNumber: '', departmentName: 'Support', isCustomizable: true };
    } else if (type === 'knowledgeNode') {
      initialData = { knowledgeTitle: 'Knowledge Vault', contextSource: '', isCustomizable: true };
    } else if (type === 'actionNode') {
      initialData = { actionType: 'whatsapp_lead', targetDestination: '', isCustomizable: true };
    } else if (type === 'intentAlertNode' || type === 'INTENT_ALERT_NODE') {
      initialData = { silentMode: true, isCustomizable: true };
    } else if (type === 'productCatalogNode' || type === 'PRODUCT_CATALOG' || type === 'product_catalog') {
      initialData = { defaultCurrency: 'LKR', allowAiDiscounts: false, isCustomizable: true };
    } else if (type === 'followUpNode' || type === 'FOLLOW_UP' || type === 'followup') {
      initialData = { delayHours: 24, quietHours: true, includeIncentive: false, filterAbandonedInquiries: true, filterAbandonedOrders: true, isEnabled: true, isCustomizable: true };
    } else if (type === 'reviewBoosterNode' || type === 'REVIEW_BOOSTER' || type === 'review_booster') {
      initialData = { googleReviewUrl: '', delayHours: 2, negativeFeedbackFirewall: true, minRatingForGoogleLink: 4, quietHours: true, isEnabled: true, isCustomizable: true };
    } else if (type === 'instructionNode') {
      initialData = { personaTitle: 'Customer Assistant', baseVoice: 'Puck', greetingVoiceSinhala: 'ආයුබෝවන්!', isCustomizable: true };
    } else if (type === 'whatsappInstanceNode' || type === 'WHATSAPP_INSTANCE') {
      const uId = Date.now().toString(36);
      const effectiveKey = tenant?.apiKey || (tenant?.id ? String(tenant.id) : uId);
      initialData = {
        displayName: '',
        phoneNumber: '',
        instanceLabel: 'WhatsApp Business Channel',
        connectionStatus: 'disconnected',
        apiMode: 'official',
        metaVerifyToken: `kangavena_wa_${effectiveKey.slice(0, 8)}_${uId}`,
        instanceName: `tenet_wa_${effectiveKey.slice(0, 8)}_${uId}`,
        enableSipCalling: true,
        sipAction: 'luna_live_answer',
        isCustomizable: true,
      };
    }

    const xPos = 350 + (nodes.length % 5) * 45;
    const yPos = 200 + (nodes.length % 5) * 45;

    const newNode: Node = {
      id: newNodeId,
      type,
      position: { x: xPos, y: yPos },
      data: initialData,
    };

    const nextNodes = nodes.concat(newNode);
    setNodes(nextNodes);
    pushHistory(nextNodes, edges);
    showToast(`Added ${type} to canvas!`, 'success');
  }, [allowTenantToolbox, nodes, edges, setNodes, pushHistory]);

  // ── Phase 32: Recycle Bin System & Diagnostics State ──
  interface RecycleBinItem {
    id: string;
    node: Node;
    deletedAt: string;
    connectedEdges: Edge[];
  }
  const [recycleBin, setRecycleBin] = useState<RecycleBinItem[]>([]);
  const [showRecycleModal, setShowRecycleModal] = useState<boolean>(false);
  const [showDiagModal, setShowDiagModal] = useState<boolean>(false);

  // ── Executive Visual Mode & Inspector State ──
  const [isCompactView, setIsCompactView] = useState<boolean>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('luna_flow_compact_mode');
      if (saved !== null) return saved === 'true';
    }
    return false; // Default to Developer / Detailed Cards mode
  });
  const [selectedInspectorNode, setSelectedInspectorNode] = useState<Node | null>(null);
  const [isInspectorExpanded, setIsInspectorExpanded] = useState<boolean>(false);
  const [showMoreMenu, setShowMoreMenu] = useState<boolean>(false);

  const toggleCompactView = useCallback(() => {
    setIsCompactView((prev) => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('luna_flow_compact_mode', String(next));
      }
      return next;
    });
  }, []);

  const handleInspectorUpdate = useCallback((nodeId: string, field: string, value: any, allUpdates?: any) => {
    setNodes((nds) => {
      const nextNodes = nds.map((n) => {
        if (n.id === nodeId) {
          const updatedData = { ...n.data, [field]: value, ...(allUpdates || {}) };
          return { ...n, data: updatedData };
        }
        return n;
      });
      return nextNodes;
    });

    setSelectedInspectorNode((prev) => {
      if (prev && prev.id === nodeId) {
        return {
          ...prev,
          data: { ...prev.data, [field]: value, ...(allUpdates || {}) },
        };
      }
      return prev;
    });
  }, [setNodes]);

  // ── Pre-Flight Flow Health Diagnostics Engine ──
  const flowHealthDiagnostics = useMemo(() => {
    return evaluateFlowHealthDiagnostics(nodes, edges);
  }, [nodes, edges]);

  const handleAutoWireAllDisconnected = useCallback(() => {

    // 3. Resolve Primary Luna Core Hub
    const currentNodes = typeof getNodes === 'function' ? getNodes() : nodes;
    const currentEdges = typeof getEdges === 'function' ? getEdges() : edges;

    const coreNodes = currentNodes.filter(n => getCanonicalNodeType(n) === 'core');
    if (coreNodes.length === 0) {
      setToast({ type: 'error', message: '❌ Missing Central Luna Core Hub Node. Please add a Luna Core Hub first!' });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    const primaryCore = coreNodes.reduce((best, current) => {
      const bestEdges = currentEdges.filter(e => e.source === best.id || e.target === best.id).length;
      const currentEdgesCount = currentEdges.filter(e => e.source === current.id || e.target === current.id).length;
      return currentEdgesCount > bestEdges ? current : best;
    }, coreNodes[0]);

    // 4. Resolve non-core capability nodes
    const nonCoreNodes = currentNodes.filter(n => n.id !== primaryCore.id);
    if (nonCoreNodes.length === 0) {
      setToast({ type: 'success', message: 'Flow graph has no external capability nodes to wire.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    let wireModificationsCount = 0;
    let nextEdges: Edge[] = [...currentEdges];

    for (const node of nonCoreNodes) {
      const configs = getWiringEdgeConfigs(node, primaryCore.id);
      
      for (const config of configs) {
        // Check if an edge ALREADY exists that strictly matches the correct ports
        const hasExactEdge = nextEdges.some(
          e =>
            String(e.source) === String(config.source) &&
            String(e.target) === String(config.target) &&
            String(e.sourceHandle || '') === String(config.sourceHandle) &&
            String(e.targetHandle || '') === String(config.targetHandle) &&
            e.style?.stroke === config.color
        );

        if (!hasExactEdge) {
          // Remove any corrupted or broken edges matching same direction between this node and core with wrong handles/colors
          nextEdges = nextEdges.filter(
            e =>
              !(
                String(e.source) === String(config.source) &&
                String(e.target) === String(config.target) &&
                (String(e.sourceHandle || '') !== String(config.sourceHandle) ||
                 String(e.targetHandle || '') !== String(config.targetHandle) ||
                 e.style?.stroke !== config.color)
              )
          );

          // Add the verified exact edge
          const edgeId = `e-${config.source}-${config.target}-${config.sourceHandle}-${config.targetHandle}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;
          nextEdges.push({
            id: edgeId,
            source: config.source,
            sourceHandle: config.sourceHandle,
            target: config.target,
            targetHandle: config.targetHandle,
            type: 'default',
            animated: true,
            style: {
              stroke: config.color,
              strokeWidth: 2.5,
              filter: `drop-shadow(0 0 6px ${config.color}80)`,
            },
          } as any);

          wireModificationsCount += 1;
        }
      }
    }

    setEdges(nextEdges);
    pushHistory(currentNodes, nextEdges);
    setIsDraft(true);

    if (wireModificationsCount > 0) {
      setToast({
        type: 'success',
        message: `⚡ Auto-wired and aligned ${wireModificationsCount} node(s) to Luna Core Hub! Click Save & Publish to apply.`,
      });
    } else {
      setToast({
        type: 'success',
        message: `✅ All ${nonCoreNodes.length} node(s) are already 100% wired with verified ports to Luna Core Hub!`,
      });
    }
    setTimeout(() => setToast(null), 4500);
  }, [nodes, edges, getNodes, getEdges, setEdges, pushHistory]);

  // ── Node Deletion & Recycle Bin Restoration Handlers ──
  const handleNodesDelete = useCallback((nodesToDelete: Node[]) => {
    const items: RecycleBinItem[] = nodesToDelete.map(node => {
      const relatedEdges = edges.filter(e => e.source === node.id || e.target === node.id);
      return {
        id: node.id,
        node,
        deletedAt: new Date().toLocaleTimeString(),
        connectedEdges: relatedEdges,
      };
    });

    setRecycleBin(prev => [...items, ...prev]);
    const deletedIdSet = new Set(nodesToDelete.map(n => n.id));
    setFormValues(prev => {
      const next = { ...prev };
      deletedIdSet.forEach(id => delete next[id]);
      return next;
    });
    setToast({ type: 'success', message: `Moved ${nodesToDelete.length} node(s) to Recycle Bin` });
    setTimeout(() => setToast(null), 4000);
  }, [edges]);

  const handleDeleteSelectedNode = useCallback(() => {
    const selected = nodes.filter(n => n.selected);
    if (selected.length === 0) {
      setToast({ type: 'error', message: 'Please select a node on the canvas to delete.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    const allCores = nodes.filter(n => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core' || String(n.id).includes('core'));
    const deletingCores = selected.filter(n => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core' || String(n.id).includes('core'));
    if (deletingCores.length > 0 && deletingCores.length >= allCores.length) {
      setToast({ type: 'error', message: 'At least one Central Luna Core Hub must remain on the canvas.' });
      setTimeout(() => setToast(null), 3000);
      return;
    }

    handleNodesDelete(selected);
    const selectedIds = new Set(selected.map(n => n.id));
    setNodes(prev => prev.filter(n => !selectedIds.has(n.id)));
    setEdges(prev => prev.filter(e => !selectedIds.has(e.source) && !selectedIds.has(e.target)));
  }, [nodes, handleNodesDelete, setNodes, setEdges]);

  const handleRestoreNode = useCallback((item: RecycleBinItem) => {
    setNodes(prev => {
      if (prev.some(n => n.id === item.node.id)) return prev;
      return [...prev, item.node];
    });

    if (item.connectedEdges && item.connectedEdges.length > 0) {
      setEdges(prev => {
        const existingIds = new Set(prev.map(e => e.id));
        const edgesToAdd = item.connectedEdges.filter(e => !existingIds.has(e.id));
        return [...prev, ...edgesToAdd];
      });
    }

    setRecycleBin(prev => prev.filter(r => r.id !== item.id));
    setToast({ type: 'success', message: `Restored node "${item.node.id}" back to canvas!` });
    setTimeout(() => setToast(null), 4000);
  }, [setNodes, setEdges]);

  const handleEmptyRecycleBin = useCallback(() => {
    setRecycleBin([]);
    setToast({ type: 'success', message: 'Recycle Bin emptied successfully.' });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // ── Phase 33: Drag and Drop Handlers for Tenet Node Toolbox ──
  const onDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      const type = event.dataTransfer.getData('application/reactflow-type');
      if (!type) return;

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const newNodeId = `node-${type.toLowerCase().replace('node', '')}-${Date.now().toString(36)}`;
      let initialData: any = { isCustomizable: true };

      if (type === 'lunaCoreNode' || type === 'LUNA_CORE_HUB' || type === 'coreNode') {
        initialData = {
          agentName: 'Luna AI',
          avatarFaceId: 'cute_chibi_bot',
          baseVoice: 'Kore',
          languageMode: 'bilingual',
          temperature: 0.7,
          isActive: true,
          isCustomizable: true,
        };
      } else if (type === 'paymentGatewayNode' || type === 'PAYMENT_GATEWAY') {
        initialData = { bankName: '', branchName: '', accountName: '', accountNumber: '', paymentLinkUrl: '', isCustomizable: true };
      } else if (type === 'callTransferNode' || type === 'CALL_TRANSFER') {
        initialData = { transferPhoneNumber: '', departmentName: 'Support', isCustomizable: true };
      } else if (type === 'knowledgeNode') {
        initialData = { knowledgeTitle: 'Knowledge Vault', contextSource: '', isCustomizable: true };
      } else if (type === 'actionNode') {
        initialData = { actionType: 'whatsapp_lead', targetDestination: '', isCustomizable: true };
      } else if (type === 'instructionNode') {
        initialData = { personaTitle: 'Customer Assistant', baseVoice: 'Puck', greetingVoiceSinhala: 'ආයුබෝවන්!', isCustomizable: true };
      } else if (type === 'callGuardNode' || type === 'CALL_GUARD' || type === 'call_guard') {
        initialData = {
          autoHangupSilence: true,
          silenceSeconds: 15,
          antiTokenBurn: true,
          antiDataLeak: true,
          maxCallDurationMinutes: 10,
          politeHangupPhrase: 'ගනුදෙනුකරුවන් රැසක් රැඳී සිටින බැවින් සහ මෙම විෂය අපගේ ව්‍යාපාරික සේවාවන්ට අදාළ නොවන බැවින් මා ඇමතුම අවසන් කරමි. ස්තූතියි, සුබ දවසක්!',
          autoCleanStaleSessions: true,
          isCustomizable: true,
        };
      } else if (type === 'securityFilterNode' || type === 'SECURITY_FILTER') {
        initialData = {
          forbiddenTopics: 'Competitor brands, Unofficial discounts, Internal profit margins',
          antiHallucination: true,
          piiShield: true,
          isCustomizable: true,
        };
      } else if (type === 'permissionNode') {
        initialData = { canViewCost: false, canModifyPrices: false, canBookRoom: true, shieldPII: true, isCustomizable: true };
      } else if (type === 'scraperNode') {
        initialData = { targetUrl: '', autoSync: true, isCustomizable: true, maxDailyScrapes: 3 };
      } else if (type === 'mediaVaultNode' || type === 'MEDIA_VAULT') {
        initialData = { assets: [], isCustomizable: true };
      } else if (type === 'whatsappInstanceNode' || type === 'WHATSAPP_INSTANCE') {
        const uId = Date.now().toString(36);
        const effectiveKey = tenant?.apiKey || (tenant?.id ? String(tenant.id) : uId);
        initialData = {
          displayName: '',
          phoneNumber: '',
          instanceLabel: 'WhatsApp Business Channel',
          connectionStatus: 'disconnected',
          apiMode: 'official',
          metaVerifyToken: `kangavena_wa_${effectiveKey.slice(0, 8)}_${uId}`,
          instanceName: `tenet_wa_${effectiveKey.slice(0, 8)}_${uId}`,
          enableSipCalling: true,
          sipAction: 'luna_live_answer',
          isCustomizable: true,
        };
      } else if (type === 'databaseNode' || type === 'DATABASE_STORAGE' || type === 'DATABASE_NODE') {
        initialData = { collectionName: 'client_leads_2026', tableCategory: 'crm_leads', autoIndexFields: true, enableCsvSync: true, isCustomizable: true };
      } else if (type === 'formGeneratorNode' || type === 'FORM_GENERATOR') {
        initialData = {
          documentTitle: 'Sri Lanka Income Tax Assessment 2026',
          documentContent: '',
          schemaType: 'tax_form',
          autoGenerateSchema: true,
          enableStepByStepInterrogation: true,
          enableMissedFieldRecovery: true,
          requireCustomerConfirmation: true,
          ownerAlertPhone: '',
          sections: [
            {
              id: 'sec_tax_identity',
              title: '1. Taxpayer Identification',
              description: 'Personal details and Tax Identification Number',
              order: 1,
              fields: [
                { id: 'full_name', label: 'Full Legal Name', questionPrompt: 'ඔබගේ සම්පූර්ණ නම (Full Name) සඳහන් කළ හැකිද?', type: 'text', required: true, order: 1 },
                { id: 'nic_passport', label: 'NIC or Passport Number', questionPrompt: 'ඔබගේ ජාතික හැඳුනුම්පත් අංකය (NIC) ලබාදිය හැකිද?', type: 'text', required: true, order: 2 },
                { id: 'tin_number', label: 'TIN (Taxpayer Identification No)', questionPrompt: 'ඔබගේ දේශීය ආදායම් දෙපාර්තමේන්තුවේ TIN අංකය තිබේද? තිබේ නම් සඳහන් කරන්න.', type: 'text', required: true, order: 3 },
                { id: 'contact_phone', label: 'Contact WhatsApp Phone', questionPrompt: 'ඔබව සම්බන්ධ කරගත හැකි WhatsApp දුරකථන අංකය කුමක්ද?', type: 'phone', required: true, order: 4 },
              ]
            },
            {
              id: 'sec_tax_income',
              title: '2. Annual Income Sources',
              description: 'Employment, Business, and Investment earnings for the tax year',
              order: 2,
              fields: [
                { id: 'employment_type', label: 'Primary Income Category', questionPrompt: 'ඔබගේ ප්‍රධාන ආදායම් මාර්ගය කුමක්ද? (Salaried Employee, Business Owner, Freelancer)?', type: 'select', options: ['Salaried Employee', 'Business Owner', 'Freelancer / Independent', 'Rent / Investment'], required: true, order: 1 },
                { id: 'gross_annual_income', label: 'Gross Annual Income (LKR)', questionPrompt: 'පසුගිය වසර සඳහා ඔබගේ මුළු වාර්ෂික ආදායම (Gross Annual Income) දළ වශයෙන් කීයද?', type: 'currency', required: true, order: 2 },
                { id: 'other_earnings', label: 'Other Earnings / Bonuses (LKR)', questionPrompt: 'වෙනත් අමතර ආදායම් හෝ බෝනස් ලැබුණාද? තිබේ නම් අගය සඳහන් කරන්න.', type: 'currency', required: false, order: 3 },
              ]
            },
            {
              id: 'sec_tax_deductions',
              title: '3. Reliefs & Qualifying Deductions',
              description: 'Loan interest, insurance, and medical claims',
              order: 3,
              fields: [
                { id: 'housing_loan_interest', label: 'Housing Loan Interest (LKR)', questionPrompt: 'නිවාස ණය සඳහා පොලී ගෙවීම් තිබේද? (තිබේ නම් අගය සඳහන් කරන්න)', type: 'currency', required: false, order: 1 },
                { id: 'life_medical_insurance', label: 'Insurance Premiums (LKR)', questionPrompt: 'ජීවිත හෝ වෛද්‍ය රක්ෂණ වාරික ගෙවා තිබේද?', type: 'currency', required: false, order: 2 },
                { id: 'declaration_confirmed', label: 'Applicant Declaration', questionPrompt: 'ඉහත ලබාදුන් තොරතුරු සත්‍ය හා නිවැරදි බව ඔබ තහවුරු කරනවාද?', type: 'boolean', required: true, order: 3 },
              ]
            }
          ],
          isCustomizable: true
        };
      } else if (type === 'voiceTypingNode' || type === 'VOICE_TYPING') {
        initialData = { enableVoiceTyping: true, targetFormSelector: '#active-genui-form', autoSubmitOnConfirmation: true, isCustomizable: true };
      } else if (type === 'sectionNode' || type === 'SECTION_NODE' || type === 'SECTION') {
        initialData = { sectionTitle: 'New Page Section', matchRoute: '/example-route', sectionPersonaOverride: '', isCustomizable: true };
      } else if (type === 'emailSmtpNode' || type === 'EMAIL_SMTP') {
        initialData = {
          smtpHost: 'smtp.gmail.com',
          smtpPort: '587',
          smtpUser: '',
          smtpPassword: '',
          senderName: 'Support Team',
          triggerEvent: 'on_user_register',
          subjectTemplate: 'Welcome to {{tenantName}} Client Portal',
          isCustomizable: true,
        };
      } else if (type === 'authGatewayNode' || type === 'AUTH_GATEWAY') {
        initialData = {
          authType: 'login_and_register',
          triggerIntent: 'User asks to sign in, check tax return status, access bank balance, or open client portal',
          modalTitle: 'Client Portal Security Gateway',
          successRedirect: '/portal/{{tenantId}}',
          enableVoiceTrigger: true,
          isCustomizable: true,
        };
      } else if (type === 'authGuardNode' || type === 'AUTH_GUARD') {
        initialData = {
          authType: 'login_and_register',
          guardMessage: 'Please authenticate to access your secure client records and tax filing dashboard.',
          guardLevel: 'strict_zero_trust',
          successRedirect: '/portal/{{tenantId}}',
          isCustomizable: true,
        };
      } else if (type === 'lunaMemoryNode' || type === 'LUNA_MEMORY' || type === 'MEMORY_VAULT') {
        initialData = { isCustomizable: true };
      } else if (type === 'followUpNode' || type === 'FOLLOW_UP' || type === 'followup') {
        initialData = { delayHours: 24, quietHours: true, includeIncentive: false, filterAbandonedInquiries: true, filterAbandonedOrders: true, isEnabled: true, isCustomizable: true };
      } else if (type === 'reviewBoosterNode' || type === 'REVIEW_BOOSTER' || type === 'review_booster') {
        initialData = { googleReviewUrl: '', delayHours: 2, negativeFeedbackFirewall: true, minRatingForGoogleLink: 4, quietHours: true, isEnabled: true, isCustomizable: true };
      } else if (type === 'webWidgetNode' || type === 'WEB_WIDGET_NODE' || type === 'web_widget' || type === 'webWidget') {
        initialData = { allowedDomains: '', overrideAgentName: '', overrideAvatarUrl: '', welcomeNote: '', themeColor: '#06b6d4', position: 'bottom-right', voiceEnabled: true, flightSoundEnabled: true, isEnabled: true, isCustomizable: true };
      }

      const newNode: Node = {
        id: newNodeId,
        type,
        position,
        data: initialData,
      };

      setNodes((nds) => nds.concat(newNode));
      showToast(`Added new ${type} node to canvas!`, 'success');
    },
    [screenToFlowPosition, setNodes, allowTenantToolbox]
  );

  // ── Phase 33: AI Auto-Flow Generator Handler ──
  const handleAutoFlowGenerate = async () => {
    if (!autoFlowPrompt.trim() || autoFlowPrompt.trim().length < 10) {
      setAutoFlowError('Please describe your business flow in at least 10 characters.');
      return;
    }

    setAutoFlowGenerating(true);
    setAutoFlowError(null);
    setAutoFlowSuccess(null);

    try {
      const data = await fetchJsonSafe('/api/tenet/widgets/auto-flow', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ prompt: autoFlowPrompt.trim() }),
      });

      if (data.success && Array.isArray(data.nodes) && Array.isArray(data.edges)) {
        setNodes(data.nodes);
        setEdges(data.edges);

        setAutoFlowSuccess(
          `✨ AI Auto-Flow generated ${data.nodeCount} nodes & ${data.edgeCount} edges — "${data.flowName || 'Custom Flow'}"!`
        );

        showToast(`✨ Flow architected successfully! Click "Save Changes" to apply.`, 'success');
        setTimeout(() => fitView({ padding: 0.2, duration: 500 }), 200);
      } else {
        setAutoFlowError(data.error || 'Failed to generate flow.');
      }
    } catch (err: any) {
      setAutoFlowError(err?.message || 'Network error during AI Auto-Flow generation.');
    } finally {
      setAutoFlowGenerating(false);
    }
  };

  const getAuthHeaders = useCallback((): Record<string, string> => {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    const effectiveToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_token') || localStorage.getItem('admin_token') : null);
    if (effectiveToken) {
      headers['Authorization'] = effectiveToken.startsWith('Bearer ') ? effectiveToken : `Bearer ${effectiveToken}`;
    }
    const effectiveApiKey = tenant?.apiKey || (typeof localStorage !== 'undefined' ? localStorage.getItem('tenet_api_key') : null);
    if (effectiveApiKey) {
      headers['x-tenant-api-key'] = effectiveApiKey;
    }
    return headers;
  }, [token, tenant]);

  // Safe JSON API Fetcher with automatic retry to prevent SyntaxError from HTML/SPA fallbacks or service restarts (502/503)
  const fetchJsonSafe = useCallback(async (url: string, init?: RequestInit, retries = 3, delayMs = 1200): Promise<any> => {
    try {
      const res = await fetch(url, init);
      const contentType = res.headers.get('content-type') || '';
      
      // If service is restarting (502/503/504) or non-JSON returned, retry gracefully
      if ((res.status === 502 || res.status === 503 || res.status === 504 || !contentType.includes('application/json')) && retries > 0) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return fetchJsonSafe(url, init, retries - 1, delayMs * 1.5);
      }

      if (!contentType.includes('application/json')) {
        throw new Error(`API format error: Server returned ${res.status} with non-JSON response.`);
      }
      const data = await res.json();
      return data;
    } catch (err: any) {
      if (retries > 0 && !err.message?.includes('401') && !err.message?.includes('403')) {
        await new Promise((resolve) => setTimeout(resolve, delayMs));
        return fetchJsonSafe(url, init, retries - 1, delayMs * 1.5);
      }
      throw err;
    }
  }, []);

  // ── 1. Fetch Dynamic Flow & Widget Data ──
  const loadWorkspaceGraph = useCallback(async (isSilent = false) => {
    if (!isSilent) setLoading(true);
    else setRefreshing(true);
    setWorkspaceError(null);

    try {
      const data = await fetchJsonSafe('/api/tenet/widgets', {
        headers: getAuthHeaders(),
      });

      if (data.success) {
        setFlowMeta(data.flow || null);
        setAllowTenantAutoFlow(true);
        setAllowTenantToolbox(true);

        const rawNodes: any[] = data.flow?.nodes || data.nodes || [];
        let rawEdges: any[] = data.flow?.edges || data.edges || [];
        if (typeof rawEdges === 'string') {
          try { rawEdges = JSON.parse(rawEdges); } catch { rawEdges = []; }
        }
        const widgetMap: Record<string, any> = data.widgetDataMap || {};

        const initialFormValues: Record<string, Record<string, any>> = {};

        const flowNodes: Node[] = rawNodes.map((n: any) => {
          const nodeId = String(n.id || '');
          const saved = widgetMap[nodeId]?.widgetData || {};
          initialFormValues[nodeId] = {
            ...(n.data || {}),
            ...saved,
            isCustomizable: true,
          };

          let nodeType = (n.type || 'actionNode');
          const isKnownType = !!customNodeTypes[nodeType as keyof typeof customNodeTypes];
          if (!isKnownType) {
            // Preserve original type in data for generic display
            nodeType = 'actionNode'; // fallback renderer
          }

          return {
            id: nodeId,
            type: nodeType,
            position: n.position || { x: 250, y: 150 },
            data: {
              ...(n.data || {}),
              ...(saved || {}),
              isCustomizable: true,
              widgetData: saved,
              isConfigured: Object.keys(saved).length > 0,
              // Pass original type for display in fallback nodes
              nodeType: isKnownType ? undefined : (n.type || 'custom'),
              label: saved?.label || n.data?.label || saved?.personaTitle || n.data?.personaTitle || saved?.sectionTitle || n.data?.sectionTitle || saved?.agentName || n.data?.agentName || (isKnownType ? undefined : (n.type || 'Flow Module')),
            },
          };
        });

        const flowEdges: Edge[] = rawEdges.map((e: any, idx: number) => {
          const srcNode = rawNodes.find((n: any) => String(n.id) === String(e.source));
          const tgtNode = rawNodes.find((n: any) => String(n.id) === String(e.target));

          let sourceHandle = e.sourceHandle;
          let targetHandle = e.targetHandle;

          // Smart fallback for sourceHandle if missing
          if (!sourceHandle && srcNode) {
            const st = (srcNode.type || '').toLowerCase();
            if (st.includes('instruction')) sourceHandle = 'out-instruction';
            else if (st.includes('knowledge')) sourceHandle = 'out-knowledge';
            else if (st.includes('memory')) sourceHandle = 'out-memory';
            else if (st.includes('mediavault') || st.includes('media_vault')) sourceHandle = 'out-media-vault';
            else if (st.includes('scraper')) sourceHandle = 'out-scraper';
            else if (st.includes('permission')) sourceHandle = 'out-permission';
            else if (st.includes('security')) sourceHandle = 'out-security';
            else if (st.includes('callguard') || st.includes('call_guard')) sourceHandle = 'out-security';
            else if (st.includes('formgenerator') || st.includes('form_generator')) sourceHandle = 'out-form-gen';
            else if (st.includes('voicetyping') || st.includes('voice_typing')) sourceHandle = 'out-voice-typing';
            else if (st.includes('section')) sourceHandle = 'out-section';
            else if (st.includes('emailsmtp') || st.includes('email_smtp')) sourceHandle = 'out-email';
            else if (st.includes('database') || st.includes('storage')) sourceHandle = 'out-database';
            else if (st.includes('payment')) sourceHandle = 'out-payment';
            else if (st.includes('calltransfer') || st.includes('call_transfer')) sourceHandle = 'out-call-transfer';
            else if (st.includes('authgateway') || st.includes('auth_gateway')) sourceHandle = 'out-auth';
            else if (st.includes('authguard') || st.includes('auth_guard')) sourceHandle = 'out-secure-zone';
            else if (st.includes('whatsapp')) sourceHandle = 'out-whatsapp-instance';
            else if (st.includes('action')) sourceHandle = 'out-action';
            else if (st.includes('core')) {
              if (tgtNode) {
                const tt = (tgtNode.type || '').toLowerCase();
                if (tt.includes('whatsapp')) sourceHandle = 'out-whatsapp';
                else if (tt.includes('action')) sourceHandle = 'out-actions';
                else if (tt.includes('pos')) sourceHandle = 'out-pos';
                else if (tt.includes('database') || tt.includes('storage')) sourceHandle = 'out-database';
                else if (tt.includes('payment')) sourceHandle = 'out-payment';
                else if (tt.includes('calltransfer') || tt.includes('call_transfer')) sourceHandle = 'out-call-transfer';
                else if (tt.includes('webhook')) sourceHandle = 'out-webhook';
                else sourceHandle = 'out-core';
              } else {
                sourceHandle = 'out-core';
              }
            }
          }

          // Smart fallback for targetHandle if missing
          if (!targetHandle && tgtNode) {
            const tt = (tgtNode.type || '').toLowerCase();
            if (tt.includes('core')) {
              if (srcNode) {
                const st = (srcNode.type || '').toLowerCase();
                if (st.includes('instruction')) targetHandle = 'in-instructions';
                else if (st.includes('knowledge')) targetHandle = 'in-knowledge';
                else if (st.includes('memory')) targetHandle = 'in-memory';
                else if (st.includes('mediavault') || st.includes('media_vault')) targetHandle = 'in-knowledge';
                else if (st.includes('scraper')) targetHandle = 'in-scraper';
                else if (st.includes('permission')) targetHandle = 'in-security';
                else if (st.includes('security')) targetHandle = 'in-security';
                else if (st.includes('callguard') || st.includes('call_guard')) targetHandle = 'in-security';
                else if (st.includes('section')) targetHandle = 'in-section-route';
                else if (st.includes('formgenerator') || st.includes('form_generator')) targetHandle = 'in-instructions';
                else if (st.includes('voicetyping') || st.includes('voice_typing')) targetHandle = 'in-instructions';
                else if (st.includes('whatsapp')) targetHandle = 'in-core';
                else if (st.includes('database') || st.includes('storage')) targetHandle = 'in-core';
                else if (st.includes('payment')) targetHandle = 'in-core';
                else if (st.includes('calltransfer') || st.includes('call_transfer')) targetHandle = 'in-core';
                else targetHandle = 'in-core';
              } else {
                targetHandle = 'in-core';
              }
            } else if (tt.includes('action')) targetHandle = 'in-action';
            else if (tt.includes('whatsapp')) targetHandle = 'in-whatsapp';
            else if (tt.includes('database') || tt.includes('storage')) targetHandle = 'in-database';
            else if (tt.includes('payment')) targetHandle = 'in-payment';
            else if (tt.includes('calltransfer') || tt.includes('call_transfer')) targetHandle = 'in-call-transfer';
            else if (tt.includes('callguard') || tt.includes('call_guard')) targetHandle = 'in-security';
            else if (tt.includes('formgenerator') || tt.includes('form_generator')) targetHandle = 'in-form-doc';
            else if (tt.includes('voicetyping') || tt.includes('voice_typing')) targetHandle = 'in-voice-typing';
            else if (tt.includes('emailsmtp') || tt.includes('email_smtp')) targetHandle = 'in-email';
            else if (tt.includes('authgateway') || tt.includes('auth_gateway')) targetHandle = 'in-auth';
            else if (tt.includes('authguard') || tt.includes('auth_guard')) targetHandle = 'in-auth';
            else if (tt.includes('section')) targetHandle = 'in-section-form';
            else if (tt.includes('knowledge')) targetHandle = 'in-knowledge';
            else if (tt.includes('instruction')) targetHandle = 'in-instruction';
            else if (tt.includes('scraper')) targetHandle = 'in-scraper';
            else if (tt.includes('permission')) targetHandle = 'in-permission';
            else if (tt.includes('security')) targetHandle = 'in-security';
            else if (tt.includes('mediavault') || tt.includes('media_vault')) targetHandle = 'in-media-vault';
            else if (tt.includes('memory')) targetHandle = 'in-memory';
          }

          return {
            id: e.id || `e-${idx}`,
            source: e.source,
            target: e.target,
            sourceHandle: sourceHandle || undefined,
            targetHandle: targetHandle || undefined,
            type: e.type || 'default',
            animated: e.animated !== undefined ? e.animated : true,
            style: e.style || {
              stroke: '#06b6d4',
              strokeWidth: 2.5,
              filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.6))',
            },
            data: e.data,
          };
        });

        setNodes(flowNodes);
        setEdges(flowEdges);
        setFormValues(initialFormValues);

        // Initialize Published Snapshot & History Stack
        publishedSnapshotRef.current = {
          nodes: JSON.parse(JSON.stringify(flowNodes)),
          edges: JSON.parse(JSON.stringify(flowEdges)),
        };
        historyRef.current = [{
          nodes: JSON.parse(JSON.stringify(flowNodes)),
          edges: JSON.parse(JSON.stringify(flowEdges)),
        }];
        historyIndexRef.current = 0;
        setIsDraft(false);
        setCanUndo(false);
        setCanRedo(false);

        setTimeout(() => {
          fitView({ padding: 0.3, duration: 500 });
        }, 100);
      } else {
        setWorkspaceError(data.error || 'Failed to load dynamic agent flow');
        showToast(data.error || 'Failed to load dynamic agent flow', 'error');
      }
    } catch (err: any) {
      console.error('[TenetDynamicWorkspace] Fetch error:', err);
      const errMsg = err?.message || 'Network error while connecting to Tenet Agent Flow.';
      setWorkspaceError(errMsg);
      showToast(errMsg, 'error');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders, fitView, fetchJsonSafe]);

  // ── 2. Fetch Leads for LEAD_TAB ──
  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const data = await fetchJsonSafe('/api/tenet/forms/submissions', {
        headers: getAuthHeaders(),
      });
      if (data.success && Array.isArray(data.submissions)) {
        setLeadsList(data.submissions);
      }
    } catch (err) {
      console.warn('[TenetDynamicWorkspace] Leads fetch error:', err);
    } finally {
      setLeadsLoading(false);
    }
  }, [getAuthHeaders, fetchJsonSafe]);

  useEffect(() => {
    loadWorkspaceGraph();
    fetchLeads();
  }, [loadWorkspaceGraph, fetchLeads]);

  // ── 3. Handle Node Selection in Canvas (ZERO FLICKER DECOUPLED) ──
  const handleNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    setSelectedInspectorNode(node);
  }, []);

  // ── Field Change Handler (used by scraper and widget persistence) ──
  const handleFieldChange = useCallback((nodeId: string, field: string, value: any) => {
    setNodes((nds) => {
      const nextNodes = nds.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, [field]: value } } : n));
      pushHistory(nextNodes, edges);
      return nextNodes;
    });
    setFormValues((prev) => ({
      ...prev,
      [nodeId]: {
        ...(prev[nodeId] || {}),
        [field]: value,
      },
    }));
  }, [setNodes, edges, pushHistory]);

  const edgeReconnectSuccessful = React.useRef(true);

  // ── Wiring Connections ──
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) => {
        const nextEdges = addEdge(
          {
            ...params,
            animated: true,
            style: {
              stroke: '#06b6d4',
              strokeWidth: 2.5,
              filter: 'drop-shadow(0 0 8px rgba(6,182,212,0.6))',
            },
          } as any,
          eds
        );
        pushHistory(nodes, nextEdges);
        return nextEdges;
      });
    },
    [setEdges, nodes, pushHistory]
  );

  // ── Edge Reconnect / Unplug Handlers ──
  const onReconnectStart = useCallback(() => {
    edgeReconnectSuccessful.current = false;
  }, []);

  const onReconnect = useCallback(
    (oldEdge: Edge, newConnection: Connection) => {
      edgeReconnectSuccessful.current = true;
      setEdges((els) => {
        const nextEdges = reconnectEdge(oldEdge, newConnection, els);
        pushHistory(nodes, nextEdges);
        return nextEdges;
      });
    },
    [setEdges, nodes, pushHistory]
  );

  const onReconnectEnd = useCallback(
    (_: any, edge: Edge) => {
      if (!edgeReconnectSuccessful.current) {
        setEdges((eds) => {
          const nextEdges = eds.filter((e) => e.id !== edge.id);
          pushHistory(nodes, nextEdges);
          return nextEdges;
        });
      }
      edgeReconnectSuccessful.current = true;
    },
    [setEdges, nodes, pushHistory]
  );

  const handleNodeDragStop = useCallback(() => {
    const currentNodes = typeof getNodes === 'function' ? getNodes() : nodes;
    const currentEdges = typeof getEdges === 'function' ? getEdges() : edges;
    pushHistory(currentNodes, currentEdges);
  }, [getNodes, getEdges, nodes, edges, pushHistory]);

  // ── Save & Publish Flow (Publishes Working Draft to Live Production Runtime) ──
  const handleSaveAll = useCallback(async () => {
    setSavingAll(true);
    try {
      const currentNodes = typeof getNodes === 'function' ? getNodes() : nodes;
      const currentEdges = typeof getEdges === 'function' ? getEdges() : edges;

      const payload = currentNodes.map((n) => {
        const liveData = {
          ...(n.data || {}),
          ...(formValues[n.id] || {}),
          ...(n.data || {}),
        };
        return {
          nodeId: n.id,
          widgetType: n.type,
          widgetData: liveData,
        };
      });

      const res = await fetchJsonSafe('/api/tenet/widgets/batch-save', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          widgets: payload,
          nodes: currentNodes.map((n) => ({
            id: n.id,
            type: n.type,
            position: n.position,
            data: {
              ...(n.data || {}),
              ...(formValues[n.id] || {}),
              ...(n.data || {}),
            },
          })),
          edges: currentEdges,
        }),
      });

      if (res.success) {
        const syncedFormValues: Record<string, Record<string, any>> = {};
        currentNodes.forEach((n) => {
          syncedFormValues[n.id] = { ...(n.data || {}) };
        });
        setFormValues(syncedFormValues);

        // Update published snapshot
        publishedSnapshotRef.current = {
          nodes: JSON.parse(JSON.stringify(currentNodes)),
          edges: JSON.parse(JSON.stringify(currentEdges)),
        };
        setIsDraft(false);
        showToast('🚀 Flow published to live production successfully!', 'success');
      } else {
        showToast(res.error || 'Failed to save widget data.', 'error');
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error saving data.', 'error');
    } finally {
      setSavingAll(false);
    }
  }, [formValues, nodes, edges, getNodes, getEdges, fetchJsonSafe, getAuthHeaders, showToast]);

  // ── Fetch Scrape Quota ──
  const fetchScrapeQuota = useCallback(async () => {
    try {
      const data = await fetchJsonSafe('/api/tenet/widgets/scrape-quota', { headers: getAuthHeaders() });
      if (data.success && data.quota) {
        setScrapeQuota(data.quota);
      }
    } catch (err) {
      console.warn('Failed to fetch scrape quota:', err);
    }
  }, [getAuthHeaders, fetchJsonSafe]);

  useEffect(() => {
    fetchScrapeQuota();
  }, [fetchScrapeQuota]);

  // ── Trigger Website Scraping via Dedicated Gemini Training Key ──
  const handleTriggerScrape = async (nodeId: string, urlToScrape: string) => {
    const cleanUrl = (urlToScrape || '').trim();
    if (!cleanUrl) {
      showToast('Please provide a valid website URL to scrape', 'error');
      return;
    }

    setScrapingNodeId(nodeId);
    showToast('AI is crawling website and synthesizing RAG knowledge...', 'success');

    try {
      const data = await fetchJsonSafe('/api/tenet/widgets/scrape-website', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ url: cleanUrl, nodeId }),
      });

      if (data.success) {
        showToast('Website scraped & AI RAG Knowledge Vault synced successfully!', 'success');
        if (data.knowledge) {
          setScrapedKnowledgePreview(data.knowledge);
        }
        if (data.quota) {
          setScrapeQuota(data.quota);
        }
        handleFieldChange(nodeId, 'url', cleanUrl);
        handleFieldChange(nodeId, 'targetUrl', cleanUrl);
        handleFieldChange(nodeId, 'lastScrapedAt', data.lastScrapedAt || new Date().toISOString());
        handleFieldChange(nodeId, 'status', 'SYNCED');
        handleFieldChange(nodeId, 'summary', data.knowledge?.slice(0, 400));

        setNodes(prev =>
          prev.map(n =>
            n.id === nodeId
              ? { ...n, data: { ...n.data, isConfigured: true, lastScrapedAt: new Date().toISOString() } }
              : n
          )
        );
      } else {
        showToast(data.error || 'Failed to scrape website.', 'error');
        if (data.quota) {
          setScrapeQuota(data.quota);
        }
      }
    } catch (err: any) {
      showToast(err?.message || 'Network error during website scraping', 'error');
    } finally {
      setScrapingNodeId(null);
    }
  };

  // ── 6. Batch Save All Nodes ──

  // ── Currently Inspected Node Helper ──

  // ── Effective Tenant ID for Embed Script ──
  const effectiveTenantId = tenant?.apiKey || tenant?.tenantId || (tenant?.id ? String(tenant.id) : 'YOUR_TENANT_ID');
  const embedSnippet = `<script src="${typeof window !== 'undefined' ? window.location.origin : 'https://kangavena.com'}/luna-agent.js?tenantId=${effectiveTenantId}" data-tenant-id="${effectiveTenantId}" defer></script>`;

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(embedSnippet);
    setCopiedEmbedCode(true);
    showToast('Embed snippet copied to clipboard!', 'success');
    setTimeout(() => setCopiedEmbedCode(false), 3000);
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PROPERTY INSPECTOR FORM RENDERER
  // ═══════════════════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════════════════
  // MAIN WORKSPACE CANVAS RENDER (FULL-BLEED VIEWPORT)
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="w-full h-full relative overflow-hidden bg-[#090D16] select-none">
      
      {/* Global Toast Notification (Positioned below top HUD bar) */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] px-4 py-3 rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.85)] backdrop-blur-2xl border flex items-center gap-3 text-xs font-sans font-bold tracking-wide animate-in fade-in slide-in-from-top-3 duration-200 ${
          toast.type === 'success'
            ? 'bg-[#121826]/95 border-cyan-500/50 text-cyan-200 shadow-cyan-950/40'
            : 'bg-[#1f1218]/95 border-rose-500/50 text-rose-200 shadow-rose-950/40'
        }`}>
          {toast.type === 'success' ? <Sparkles className="w-4 h-4 text-cyan-400 animate-spin" /> : <AlertCircle className="w-4 h-4 text-rose-400" />}
          <span>{toast.message}</span>
        </div>
      )}

      {/* ── World-Class Executive Top HUD Bar (Sovereign Spec) ── */}
      <div className="lg-glass absolute top-2 left-2 right-2 sm:top-4 sm:left-4 sm:right-4 z-30 min-h-[48px] sm:h-14 bg-gradient-to-r from-[#080B14]/95 via-[#0C1222]/95 to-[#080B14]/95 backdrop-blur-2xl border border-white/[0.12] px-2.5 sm:px-4 py-1.5 sm:py-0 rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.85),inset_0_1px_0_0_rgba(255,255,255,0.12)] flex items-center justify-between gap-2 sm:gap-3 select-none overflow-x-auto sm:overflow-visible scrollbar-none">
        
        {/* Zone 1: Flow Identity & Live Status */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <div className="flex items-center gap-1.5 sm:gap-2 bg-slate-950/70 border border-white/[0.08] px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl shrink-0 shadow-inner">
            <div className={`w-2 h-2 rounded-full ${isDraft ? 'bg-amber-400 shadow-[0_0_8px_rgba(251,191,36,0.9)] animate-ping' : 'bg-emerald-400 shadow-[0_0_8px_rgba(52,211,153,0.9)] animate-pulse'}`} />
            <span className="text-[11px] sm:text-xs font-bold text-white font-sans tracking-tight truncate max-w-[90px] sm:max-w-[150px]" title={flowMeta?.flowName || 'Agent Flow Graph'}>
              {flowMeta?.flowName || 'Agent Flow Graph'}
            </span>
          </div>

          <span className="text-[9px] sm:text-[9.5px] font-mono px-1.5 sm:px-2 py-0.5 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/30 font-bold shrink-0">
            {nodes.length} NODES
          </span>

          {isDraft ? (
            <span className="text-[8.5px] sm:text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-md bg-amber-500/15 text-amber-300 border border-amber-500/40 animate-pulse shrink-0">
              ● Draft
            </span>
          ) : (
            <span className="text-[8.5px] sm:text-[9px] font-mono font-bold uppercase tracking-wider px-1.5 sm:px-2 py-0.5 rounded-md bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 shrink-0">
              ● Live
            </span>
          )}
        </div>

        {/* Zone 2: Studio Operations & Palette */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Add Node Drawer Trigger */}
          <button
            type="button"
            onClick={() => setIsNodeDrawerOpen(prev => !prev)}
            className={`flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border shrink-0 ${
              isNodeDrawerOpen
                ? 'bg-cyan-500 text-slate-950 border-cyan-400 shadow-[0_0_20px_rgba(6,182,212,0.6)] font-black'
                : 'bg-[#101728]/90 hover:bg-[#162038] text-cyan-300 border-cyan-500/40 hover:border-cyan-300 shadow-[0_4px_15px_rgba(0,0,0,0.4)]'
            }`}
            title="Open Node Palette"
          >
            <Plus size={13} className={isNodeDrawerOpen ? 'rotate-45 transition-transform duration-200' : 'transition-transform duration-200'} />
            <span className="hidden xs:inline">Add Node</span>
            <span className="xs:hidden">Node</span>
          </button>

          {/* Starter Industry Blueprints Modal Trigger */}
          <button
            type="button"
            onClick={() => setShowBlueprintsModal(true)}
            className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl text-xs font-bold bg-gradient-to-r from-[#11192e] via-[#152342] to-[#101930] hover:from-[#16223e] hover:to-[#162344] text-white border border-cyan-500/35 hover:border-cyan-400 shadow-[0_4px_20px_rgba(0,0,0,0.5),inset_0_1px_0_rgba(255,255,255,0.1)] transition-all transform hover:scale-[1.02] cursor-pointer shrink-0 active:scale-95"
            title="Choose an industry starter blueprint"
          >
            <Building size={13} className="text-cyan-300" />
            <span className="hidden sm:inline">Blueprints</span>
          </button>

          {/* Smart Auto-Wire Button */}
          <button
            type="button"
            onClick={handleAutoWireAllDisconnected}
            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-xl text-xs font-bold transition-all cursor-pointer border shrink-0 active:scale-95 ${
              flowHealthDiagnostics.disconnectedCount > 0
                ? 'bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black shadow-[0_0_20px_rgba(245,158,11,0.5)] border-amber-400'
                : 'bg-slate-950/70 hover:bg-slate-900 text-sky-300 border-white/[0.08] hover:border-sky-500/40'
            }`}
            title="Auto-wire all disconnected canvas nodes to Luna Core Hub"
          >
            <Zap size={13} className={flowHealthDiagnostics.disconnectedCount > 0 ? 'text-slate-950 animate-pulse' : 'text-sky-400'} />
            <span className="hidden md:inline">Auto-Wire{flowHealthDiagnostics.disconnectedCount > 0 ? ` (${flowHealthDiagnostics.disconnectedCount})` : ''}</span>
            <span className="md:hidden">Wire{flowHealthDiagnostics.disconnectedCount > 0 ? ` (${flowHealthDiagnostics.disconnectedCount})` : ''}</span>
          </button>

          {/* Health Diagnostics Badge */}
          <button
            type="button"
            onClick={() => setShowDiagModal(true)}
            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-xl text-xs font-bold border transition cursor-pointer shadow-sm ${
              flowHealthDiagnostics.status === 'healthy'
                ? 'bg-slate-950/70 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/15'
                : 'bg-slate-950/70 text-amber-300 border-amber-500/40 hover:bg-amber-500/15'
            }`}
            title="Flow Health Diagnostic Inspector"
          >
            <Activity className="w-3.5 h-3.5 text-emerald-400" />
            <span className="font-mono text-[11px] sm:text-xs">{flowHealthDiagnostics.score}%</span>
          </button>

          {/* Global View Mode Switcher */}
          <button
            type="button"
            onClick={toggleCompactView}
            className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-xl text-xs font-bold border transition-all cursor-pointer shrink-0 ${
              isCompactView
                ? 'bg-purple-500/20 text-purple-300 border-purple-500/40 shadow-[0_0_15px_rgba(168,85,247,0.25)] hover:bg-purple-500/30'
                : 'bg-slate-950/70 text-cyan-300 border-white/[0.08] hover:border-cyan-500/40 shadow-sm'
            }`}
            title={isCompactView ? "Click to switch to Full Developer Cards View" : "Click to switch to Executive Compact Flow"}
          >
            <SlidersHorizontal size={13} className={isCompactView ? "text-purple-400" : "text-cyan-400"} />
            <span className="hidden sm:inline">{isCompactView ? 'Compact' : 'Detail'}</span>
          </button>
        </div>

        {/* Zone 3: Executive Actions, Overflow Menu & Primary CTAs */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          {/* Compact Undo/Redo Cluster */}
          <div className="flex items-center bg-slate-950/80 border border-white/[0.08] rounded-xl p-0.5 shrink-0 shadow-inner">
            <button
              type="button"
              onClick={handleUndo}
              disabled={!canUndo}
              className={`p-1 sm:p-1.5 rounded-lg transition cursor-pointer ${
                canUndo ? 'text-cyan-300 hover:text-white hover:bg-cyan-500/20' : 'text-slate-600 opacity-40 cursor-not-allowed'
              }`}
              title="Undo (Ctrl+Z)"
            >
              <Undo2 size={13} />
            </button>
            <div className="w-px h-3.5 bg-white/[0.08]" />
            <button
              type="button"
              onClick={handleRedo}
              disabled={!canRedo}
              className={`p-1 sm:p-1.5 rounded-lg transition cursor-pointer ${
                canRedo ? 'text-cyan-300 hover:text-white hover:bg-cyan-500/20' : 'text-slate-600 opacity-40 cursor-not-allowed'
              }`}
              title="Redo (Ctrl+Y)"
            >
              <Redo2 size={13} />
            </button>
          </div>

          {/* Overflow Menu (··· More Tools) */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowMoreMenu(prev => !prev)}
              className="p-1 sm:p-1.5 rounded-xl bg-slate-950/80 hover:bg-slate-900 text-slate-400 hover:text-white border border-white/[0.08] hover:border-white/[0.18] transition cursor-pointer shadow-sm"
              title="More Canvas Tools"
            >
              <MoreHorizontal size={15} />
            </button>

            {showMoreMenu && (
              <div className="absolute right-0 top-full mt-2 w-48 bg-[#0A0E1A]/98 backdrop-blur-2xl border border-white/[0.12] rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.85)] p-1.5 space-y-1 z-50 animate-in fade-in zoom-in-95">
                <button
                  type="button"
                  onClick={() => {
                    handleDeleteSelectedNode();
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-300 hover:bg-rose-500/20 flex items-center gap-2 transition cursor-pointer text-left"
                >
                  <Trash2 size={13} /> Delete Selected
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowRecycleModal(true);
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-300 hover:bg-amber-500/20 flex items-center justify-between transition cursor-pointer text-left"
                >
                  <span className="flex items-center gap-2">
                    <RotateCcw size={13} /> Recycle Bin
                  </span>
                  <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300">
                    {recycleBin.length}
                  </span>
                </button>
                {isDraft && (
                  <button
                    type="button"
                    onClick={() => {
                      handleDiscardDraft();
                      setShowMoreMenu(false);
                    }}
                    className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-rose-400 hover:bg-rose-500/20 flex items-center gap-2 transition cursor-pointer text-left"
                  >
                    <RotateCcw size={13} /> Discard Draft
                  </button>
                )}
                <div className="h-px bg-white/[0.08] my-1" />
                <button
                  type="button"
                  onClick={() => {
                    setShowAutoFlowModal(true);
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-sky-300 hover:bg-sky-500/20 flex items-center gap-2 transition cursor-pointer text-left"
                >
                  <Sparkles size={13} /> AI Auto-Flow
                </button>
                <button
                  type="button"
                  onClick={() => {
                    fitView({ padding: 0.3, duration: 500 });
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-300 hover:bg-white/[0.06] flex items-center gap-2 transition cursor-pointer text-left"
                >
                  <Maximize2 size={13} /> Fit View
                </button>
                <button
                  type="button"
                  onClick={() => {
                    loadWorkspaceGraph(true);
                    setShowMoreMenu(false);
                  }}
                  className="w-full px-3 py-1.5 rounded-lg text-xs font-semibold text-slate-300 hover:bg-white/[0.06] flex items-center gap-2 transition cursor-pointer text-left"
                >
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
            )}
          </div>

          {/* Save & Publish Action Button */}
          <button
            type="button"
            onClick={handleSaveAll}
            disabled={savingAll}
            className={`px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-xl text-xs font-bold flex items-center gap-1 sm:gap-1.5 transition cursor-pointer shadow-sm disabled:opacity-50 active:scale-95 border shrink-0 ${
              isDraft
                ? 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 hover:from-emerald-300 hover:to-cyan-300 text-slate-950 border-emerald-300 shadow-[0_0_25px_rgba(52,211,153,0.5)] font-black animate-pulse'
                : 'bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
            }`}
            title={isDraft ? "Publish draft changes to live production runtime" : "All changes are published to live production"}
          >
            <UploadCloud size={13} className={savingAll ? 'animate-spin text-slate-950' : isDraft ? 'text-slate-950' : 'text-emerald-400'} />
            <span className="hidden xs:inline">{savingAll ? 'Publishing…' : isDraft ? 'Save & Publish' : 'Saved & Live'}</span>
            <span className="xs:hidden">{savingAll ? '…' : isDraft ? 'Publish' : 'Saved'}</span>
          </button>

          {/* Deploy & Get Code Action Button */}
          <button
            type="button"
            onClick={() => setShowDeployModal(true)}
            className="px-2.5 sm:px-3.5 py-1 sm:py-1.5 rounded-xl text-xs font-black bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 flex items-center gap-1 sm:gap-1.5 transition cursor-pointer shadow-[0_0_20px_rgba(6,182,212,0.4)] border border-cyan-400/40 shrink-0"
          >
            <Code size={13} />
            <span className="hidden xs:inline">Deploy</span>
          </button>
        </div>

      </div>

      {/* ── Phase 34: Floating Glassmorphism Node Palette Drawer (Sovereign Spec) ── */}
      {isNodeDrawerOpen && (
        <div className="lg-glass absolute top-16 sm:top-20 left-2 sm:left-4 bottom-20 sm:bottom-6 w-[calc(100vw-1rem)] sm:w-80 max-w-[360px] z-40 bg-gradient-to-b from-[#080B14]/98 via-[#0C1222]/98 to-[#06080F]/98 backdrop-blur-2xl border border-white/[0.12] rounded-3xl shadow-[0_25px_70px_rgba(0,0,0,0.95),inset_0_1px_0_rgba(255,255,255,0.1)] flex flex-col overflow-hidden animate-in slide-in-from-left duration-200">
          {/* Drawer Header */}
          <div className="p-4 border-b border-white/[0.08] flex items-center justify-between shrink-0 bg-slate-950/60">
            <div className="flex items-center gap-2.5">
              <div className="p-2 rounded-xl bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 shadow-[0_0_15px_rgba(6,182,212,0.2)]">
                <Layers size={16} />
              </div>
              <div>
                <h3 className="text-xs font-black text-white tracking-wide">Node Palette</h3>
                <p className="text-[10px] text-slate-400">Drag or click to add capability</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1.5">
              <span className="text-[9px] font-mono font-bold px-2 py-0.5 rounded-full border bg-emerald-500/15 text-emerald-300 border-emerald-500/30 shadow-[0_0_10px_rgba(16,185,129,0.2)]">
                16 ACTIVE
              </span>
              <button
                type="button"
                onClick={() => setIsNodeDrawerOpen(false)}
                className="p-1.5 rounded-xl bg-slate-900/80 hover:bg-slate-800 text-slate-400 hover:text-white border border-white/[0.08] transition-colors cursor-pointer"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          {/* Search & Category Filters */}
          <div className="p-3 border-b border-white/[0.08] space-y-2 shrink-0 bg-slate-950/40">
            <div className="relative">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
              <input
                type="text"
                value={nodeSearchQuery}
                onChange={(e) => setNodeSearchQuery(e.target.value)}
                placeholder="Search 16 nodes (e.g. payment, form)..."
                className="w-full pl-8 pr-3 py-1.5 rounded-xl bg-slate-950/90 border border-white/[0.08] text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400/80 transition-all"
              />
            </div>

            {/* Category Pills */}
            <div className="flex items-center gap-1 overflow-x-auto pb-0.5 [&::-webkit-scrollbar]:hidden">
              {[
                { id: 'all', label: 'All' },
                { id: 'brain', label: '🧠 Brain' },
                { id: 'knowledge', label: '📚 Knowledge' },
                { id: 'actions', label: '⚡ Actions' },
                { id: 'commerce', label: '💳 Commerce' },
                { id: 'security', label: '🛡️ Security' },
              ].map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id as any)}
                  className={`px-2 py-1 rounded-lg text-[10px] font-bold shrink-0 transition-all cursor-pointer border ${
                    selectedCategory === cat.id
                      ? 'bg-cyan-500/20 border-cyan-400/50 text-cyan-200 shadow-[0_0_12px_rgba(6,182,212,0.3)]'
                      : 'bg-slate-950/80 border-white/[0.06] text-slate-400 hover:text-slate-200 hover:border-white/[0.12]'
                  }`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
          </div>

          {/* Node Cards Scroll Area with Custom Obsidian Scrollbar */}
          <div className="flex-1 overflow-y-auto p-3 space-y-2 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-thumb]:bg-slate-800 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
            {filteredPaletteNodes.length === 0 ? (
              <div className="p-6 text-center text-xs text-slate-500">
                No matching nodes found.
              </div>
            ) : (
              filteredPaletteNodes.map((item) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={item.type}
                    className={`p-3 rounded-2xl bg-gradient-to-br ${item.bgGradient} border ${item.borderColor} transition-all relative overflow-hidden group cursor-grab active:cursor-grabbing hover:scale-[1.02] shadow-lg`}
                    draggable={true}
                    onDragStart={(e) => {
                      e.dataTransfer.setData('application/reactflow-type', item.type);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 rounded-lg bg-slate-950/60 text-white">
                          <IconComponent size={14} />
                        </div>
                        <span className="text-xs font-bold text-white tracking-wide">{item.label}</span>
                      </div>
                      <span className={`text-[8px] font-mono font-black uppercase px-1.5 py-0.5 rounded border ${item.tagColor}`}>
                        {item.tag}
                      </span>
                    </div>
                    
                    <p className="text-[10px] text-slate-300 mt-1.5 leading-tight">{item.desc}</p>

                    <div className="mt-2.5 pt-2 border-t border-white/5 flex items-center justify-between">
                      <span className="text-[9px] text-slate-400 font-mono">Drag to Canvas</span>
                      <button
                        type="button"
                        onClick={() => handleAddNodeToCanvas(item.type)}
                        className="px-2 py-0.5 rounded-lg text-[10px] font-bold bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-500/40 flex items-center gap-1 transition-colors cursor-pointer"
                        title="1-Click add to canvas"
                      >
                        <Plus size={10} /> Add
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ── React Flow Full-Bleed Canvas Viewport (100% Canvas Space) ── */}
      <div className="w-full h-full relative overflow-hidden" ref={reactFlowWrapper} onDragOver={onDragOver} onDrop={onDrop}>
          {loading ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-[#090D16] space-y-3">
              <RefreshCw className="w-8 h-8 text-cyan-400 animate-spin" />
              <p className="text-xs font-bold text-slate-300 font-sans tracking-wide">Compiling Dynamic Agent Flow Graph...</p>
            </div>
          ) : workspaceError && nodes.length === 0 ? (
            <div className="w-full h-full flex flex-col items-center justify-center bg-[#090D16] p-6 text-center">
              <div className="max-w-md bg-[#0D121F] border border-red-500/40 rounded-3xl p-8 shadow-[0_0_50px_rgba(239,68,68,0.2)] space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-red-500/20 border border-red-500/40 flex items-center justify-center text-red-400 mx-auto">
                  <AlertCircle size={24} />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-100">Unable to Load Agent Graph</h3>
                  <p className="text-xs text-slate-400 mt-1 font-mono">{workspaceError}</p>
                </div>
                <button
                  type="button"
                  onClick={() => loadWorkspaceGraph()}
                  className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 font-black text-xs uppercase tracking-wider transition-all shadow-[0_0_20px_rgba(6,182,212,0.4)] cursor-pointer"
                >
                  Retry Connection
                </button>
              </div>
            </div>
          ) : (
            <TenantViewContext.Provider value={{
              ...useTenantView(),
              isCompactView,
              toggleCompactView,
              openInspector: (nodeId: string) => {
                const n = nodes.find(item => item.id === nodeId);
                if (n) {
                  setSelectedInspectorNode(n);
                  setIsInspectorExpanded(true);
                }
              }
            }}>
              <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                onNodesDelete={handleNodesDelete}
                onConnect={onConnect}
                onReconnect={onReconnect}
                onReconnectStart={onReconnectStart}
                onReconnectEnd={onReconnectEnd}
                onNodeClick={handleNodeClick}
                onPaneClick={() => {
                  setSelectedInspectorNode(null);
                  setIsInspectorExpanded(false);
                }}
                nodeTypes={customNodeTypes}
                edgeTypes={customEdgeTypes}
                nodesConnectable={true}
                nodesDraggable={true}
                elementsSelectable={true}
                edgesReconnectable={true}
                edgesFocusable={true}
                deleteKeyCode={['Backspace', 'Delete']}
                fitView
                minZoom={0.05}
                maxZoom={3}
                proOptions={{ hideAttribution: true }}
                className="bg-[#090D16]"
              >
                <Background variant={BackgroundVariant.Dots} gap={24} size={1.5} color="#182234" />
                <Controls className="!bg-[#080B14]/95 !border !border-white/[0.1] !rounded-2xl !shadow-[0_15px_40px_rgba(0,0,0,0.85)] fill-slate-300 !left-3 sm:!left-6 !bottom-20 sm:!bottom-6 backdrop-blur-xl overflow-hidden [&_button]:!border-white/[0.08] [&_button:hover]:!bg-white/[0.08]" />
                <MiniMap
                  className="!hidden md:!block !bg-[#080B14]/95 !border !border-white/[0.1] !rounded-2xl !shadow-[0_15px_40px_rgba(0,0,0,0.85)] !right-6 !bottom-22 overflow-hidden backdrop-blur-xl"
                  nodeColor="#22d3ee"
                  maskColor="rgba(8, 11, 20, 0.88)"
                />
              </ReactFlow>
            </TenantViewContext.Provider>
          )}
        </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PHASE 7: LIVE AVATAR TESTER FLOATING ACTION BUTTON (FAB)         */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      <div className="fixed bottom-20 right-3 sm:bottom-6 sm:right-6 z-40 flex items-center gap-2 sm:gap-3">
        <button
          type="button"
          onClick={() => setIsLunaTesterOpen(prev => !prev)}
          className={`group relative px-3 sm:px-4 py-2 sm:py-2.5 rounded-2xl flex items-center justify-center transition-all duration-300 cursor-pointer shadow-[0_15px_40px_rgba(0,0,0,0.85)] border active:scale-95 ${
            isLunaTesterOpen
              ? 'bg-rose-500/90 hover:bg-rose-500 border-rose-400 text-white shadow-[0_0_30px_rgba(244,63,94,0.5)]'
              : 'bg-gradient-to-r from-[#0C1222]/95 via-[#111A30]/95 to-[#0C1222]/95 backdrop-blur-2xl hover:bg-[#14203d] border-cyan-500/50 hover:border-cyan-400 text-white shadow-[0_0_25px_rgba(6,182,212,0.25)] hover:shadow-[0_0_35px_rgba(6,182,212,0.5)]'
          }`}
          title="Test Dynamic AI Voice Agent"
        >
          <div className="relative flex items-center gap-2 sm:gap-2.5">
            {isLunaTesterOpen ? (
              <>
                <X size={16} className="text-white" />
                <span className="text-xs font-bold font-sans tracking-wide">Close Tester</span>
              </>
            ) : (
              <>
                <div className="relative p-1 rounded-lg bg-cyan-500/20 text-cyan-300 border border-cyan-500/40">
                  <Bot size={16} />
                  <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse shadow-[0_0_6px_rgba(52,211,153,0.9)]" />
                </div>
                <div className="text-left">
                  <div className="text-xs font-black font-sans tracking-tight text-white flex items-center gap-1.5">
                    <span className="hidden xs:inline">TEST AVATAR</span>
                    <span className="xs:hidden">TEST</span>
                    <span className="text-[9px] font-mono font-bold px-1.5 py-0.2 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                      LIVE
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </button>
      </div>

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PHASE 7: LIVE AVATAR TESTER POPUP MODAL (MOUNT LUNAVOICEMODEL)   */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {isLunaTesterOpen && (
        <div className="lg-glass lg-3d fixed bottom-24 right-6 z-50 w-[380px] sm:w-[420px] h-[540px] rounded-3xl bg-[#0D121F]/95 backdrop-blur-2xl border border-cyan-500/40 shadow-[0_20px_70px_rgba(0,0,0,0.95)] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-200">
          
          {/* Tester Header */}
          <div className="px-5 py-3.5 bg-gradient-to-r from-[#101726] to-[#0D121F] border-b border-slate-800 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="p-1.5 rounded-xl bg-cyan-500/20 border border-cyan-500/40 text-cyan-300 shadow-[0_0_10px_rgba(6,182,212,0.4)]">
                <Bot size={16} />
              </div>
              <div>
                <h4 className="text-xs font-black text-white flex items-center gap-1.5">
                  <span>Luna Voice Agent Tester</span>
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                </h4>
                <p className="text-[10px] text-cyan-300/80 font-mono">Live Luna Flow Runtime</p>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setIsLunaTesterOpen(false)}
              className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
            >
              <X size={16} />
            </button>
          </div>

          {/* Tester Live Container */}
          <div className="flex-1 relative w-full h-full overflow-hidden bg-gradient-to-b from-[#090D16] to-[#0D121F]">
            <LunaVoiceModel
              apiKey={tenant?.apiKey || ''}
              role="tenant"
              storeName={tenant?.name || 'Tenant AI'}
              onDisconnect={() => setIsLunaTesterOpen(false)}
            />
          </div>
        </div>
      )}

      {/* ═════════════════════════════════════════════════════════════════ */}
      {/* PHASE 7: DEPLOY & EMBED CODE MODAL OVERLAY                       */}
      {/* ═════════════════════════════════════════════════════════════════ */}
      {showDeployModal && (
        <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="w-full max-w-xl bg-[#0D121F] border border-cyan-500/40 rounded-3xl p-6 sm:p-8 shadow-[0_25px_80px_rgba(0,0,0,0.9)] space-y-6 relative animate-in zoom-in-95 duration-200">
            
            {/* Modal Close Button */}
            <button
              type="button"
              onClick={() => setShowDeployModal(false)}
              className="absolute top-6 right-6 p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
            >
              <X size={18} />
            </button>

            {/* Modal Header */}
            <div className="space-y-1.5 pr-8">
              <div className="flex items-center gap-2 text-cyan-400 text-xs font-mono font-bold uppercase tracking-wider">
                <Code size={14} /> DEPLOYMENT & INTEGRATION
              </div>
              <h3 className="text-2xl font-black text-white font-sans tracking-tight">
                Deploy Luna AI Agent
              </h3>
              <p className="text-xs text-slate-400 leading-relaxed font-sans">
                Paste this one-line script snippet into your external website or HTML web application to embed your custom AI flow.
              </p>
            </div>

            {/* Target Account Info & Tenant ID Badge Row */}
            <div className="space-y-2.5 bg-[#080C14] border border-cyan-500/30 rounded-2xl p-4 shadow-inner">
              {/* Account Header Row */}
              <div className="flex items-center justify-between gap-3 pb-2 border-b border-white/5">
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-8 h-8 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center text-cyan-300 font-bold shrink-0">
                    <Shield size={15} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] uppercase tracking-widest text-slate-400 font-bold">Target Account</span>
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    </div>
                    <h4 className="text-xs font-black text-white truncate max-w-[240px] sm:max-w-[340px]">{tenant?.name || 'Tenant AI Account'}</h4>
                  </div>
                </div>
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/15 text-emerald-300 border border-emerald-400/30 shrink-0">
                  Active Environment
                </span>
              </div>

              {/* Tenant ID Value Box */}
              <div className="flex items-center justify-between gap-2 bg-[#0D121F] border border-cyan-500/20 rounded-xl px-3 py-2">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-[11px] font-bold text-slate-400 font-sans shrink-0">Tenant ID:</span>
                  <span className="font-mono text-xs font-bold text-cyan-300 truncate select-all">{tenant?.apiKey || tenant?.tenantId || tenant?.id || 'TENET_ACCOUNT'}</span>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const rawId = tenant?.apiKey || tenant?.tenantId || String(tenant?.id || '');
                    navigator.clipboard.writeText(rawId);
                    setCopiedTenantId(true);
                    showToast('Tenant ID copied to clipboard!', 'success');
                    setTimeout(() => setCopiedTenantId(false), 2500);
                  }}
                  className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-cyan-500/20 hover:bg-cyan-500/30 text-cyan-300 border border-cyan-400/30 text-[11px] font-bold transition-all cursor-pointer shrink-0"
                  title="Copy Tenant ID"
                >
                  {copiedTenantId ? (
                    <>
                      <Check size={12} className="text-emerald-400" />
                      <span className="text-emerald-400">Copied</span>
                    </>
                  ) : (
                    <>
                      <Copy size={12} />
                      <span>Copy ID</span>
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Code Snippet Box */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-slate-400 font-mono">
                <span>HTML Embed Snippet:</span>
                <span className="text-emerald-400 text-[11px] flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> Ready for Production
                </span>
              </div>

              <div className="relative bg-[#080C14] border border-cyan-500/30 rounded-2xl p-4 font-mono text-xs text-cyan-200 overflow-x-auto shadow-inner select-all">
                <code>{embedSnippet}</code>
              </div>
            </div>

            {/* Integration Instructions Card */}
            <div className="bg-[#131A2B]/80 border border-slate-800 rounded-2xl p-4 space-y-2 text-xs text-slate-300">
              <span className="font-bold text-white uppercase text-[11px] tracking-wider block">
                Quick Integration Steps:
              </span>
              <ol className="list-decimal list-inside space-y-1 text-slate-400 leading-relaxed">
                <li>Copy the script tag above.</li>
                <li>Paste it right before the closing <code className="text-cyan-300 bg-slate-950 px-1 py-0.5 rounded">&lt;/body&gt;</code> tag on your website.</li>
                <li>Your customized voice avatar & dynamic workflow will load automatically for visitors.</li>
              </ol>
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setShowDeployModal(false)}
                className="px-5 py-2.5 rounded-xl text-xs font-bold text-slate-400 hover:text-white hover:bg-slate-800 transition cursor-pointer"
              >
                Close
              </button>

              <button
                type="button"
                onClick={copyEmbedCode}
                className="px-6 py-2.5 rounded-xl text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 flex items-center gap-2 transition cursor-pointer shadow-[0_0_20px_rgba(6,182,212,0.4)]"
              >
                {copiedEmbedCode ? (
                  <>
                    <Check size={14} className="text-slate-950" /> Copied to Clipboard!
                  </>
                ) : (
                  <>
                    <Copy size={14} /> Copy Embed Code
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 32: Diagnostics Modal for Tenet Dashboard ── */}
      {showDiagModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className={`p-2.5 rounded-2xl ${flowHealthDiagnostics.status === 'healthy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}`}>
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Flow Health Diagnostics</h3>
                  <p className="text-xs text-slate-400">Health Score: {flowHealthDiagnostics.score}%</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowDiagModal(false)}
                className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <div className="p-3 rounded-2xl bg-slate-950/60 border border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-300">Total Graph Nodes:</span>
                <span className="text-xs font-bold text-white">{nodes.length} nodes</span>
              </div>
              <div className="p-3 rounded-2xl bg-slate-950/60 border border-slate-800 flex items-center justify-between">
                <span className="text-xs text-slate-300">Unplugged Nodes:</span>
                <span className={`text-xs font-bold ${flowHealthDiagnostics.disconnectedCount > 0 ? 'text-amber-400' : 'text-emerald-400'}`}>
                  {flowHealthDiagnostics.disconnectedCount} nodes
                </span>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Issues & Status ({flowHealthDiagnostics.issues.length}):</label>
                {flowHealthDiagnostics.issues.length === 0 ? (
                  <div className="p-3 rounded-xl bg-emerald-950/40 border border-emerald-500/30 text-xs text-emerald-300 flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                    All nodes are connected and operating cleanly!
                  </div>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {flowHealthDiagnostics.issues.map((iss, i) => (
                      <div key={i} className="p-2 rounded-xl bg-amber-950/30 border border-amber-500/30 text-xs text-amber-300 flex items-center gap-2">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-400 shrink-0" />
                        <span>{iss}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
              {flowHealthDiagnostics.disconnectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    handleAutoWireAllDisconnected();
                    setShowDiagModal(false);
                  }}
                  className="px-4 py-2 rounded-xl text-xs font-bold bg-cyan-500 hover:bg-cyan-400 text-slate-950 flex items-center gap-1.5 transition-all cursor-pointer"
                >
                  <Wand2 className="w-3.5 h-3.5" />
                  Auto-Wire Nodes
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowDiagModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Phase 32: Recycle Bin / Deleted Nodes Recovery Modal ── */}
      {showRecycleModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-lg bg-slate-900 border border-slate-700/80 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 rounded-2xl bg-amber-500/20 text-amber-400">
                  <RotateCcw className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">Recycle Bin (Deleted Nodes)</h3>
                  <p className="text-xs text-slate-400">Restore accidentally deleted nodes anytime</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowRecycleModal(false)}
                className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>

            <div className="space-y-2 max-h-64 overflow-y-auto">
              {recycleBin.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-400 bg-slate-950/40 rounded-2xl border border-slate-800">
                  <RotateCcw className="w-8 h-8 mx-auto mb-2 text-slate-600 opacity-50" />
                  <p className="font-semibold text-slate-300">Recycle Bin is empty</p>
                  <p className="text-[11px] text-slate-500 mt-0.5">When you delete a node from the canvas, it will be stored here safely.</p>
                </div>
              ) : (
                recycleBin.map((item) => (
                  <div
                    key={item.id}
                    className="p-3 rounded-2xl bg-slate-950/70 border border-slate-800 hover:border-slate-700 flex items-center justify-between transition-all"
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-white capitalize">{item.node.type || 'Custom Node'}</span>
                        <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-slate-800 text-cyan-300 border border-slate-700">
                          {item.id}
                        </span>
                      </div>
                      <p className="text-[10px] text-slate-400">Deleted at {item.deletedAt}</p>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRestoreNode(item)}
                      className="px-3 py-1.5 rounded-xl text-xs font-bold bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 flex items-center gap-1 transition-all cursor-pointer"
                      title="Restore this node to canvas"
                    >
                      <RotateCcw size={12} />
                      Restore
                    </button>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-slate-800">
              {recycleBin.length > 0 && (
                <button
                  type="button"
                  onClick={handleEmptyRecycleBin}
                  className="px-3 py-1.5 rounded-xl text-xs font-semibold bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 transition-colors cursor-pointer"
                >
                  Empty Trash
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowRecycleModal(false)}
                className="ml-auto px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── Phase 33: AI Auto-Flow Generator Modal for Tenet Dashboard ── */}
      {showAutoFlowModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
          <div className="relative w-full max-w-lg bg-slate-900 border border-sky-500/40 rounded-3xl p-6 shadow-2xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div className="flex items-center gap-2.5">
                <div className="p-2.5 rounded-2xl bg-sky-500/20 text-sky-400">
                  <Sparkles className="w-5 h-5 animate-pulse" />
                </div>
                <div>
                  <h3 className="text-base font-black text-white">AI Auto-Flow Architect</h3>
                  <p className="text-xs text-slate-400">Describe your business flow in Sinhala or English</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setShowAutoFlowModal(false)}
                className="p-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white transition-colors cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <textarea
                value={autoFlowPrompt}
                onChange={(e) => setAutoFlowPrompt(e.target.value)}
                placeholder="e.g. Create a bilingual hotel booking agent with WhatsApp confirmation, payment bank transfer gateway, and call transfer to human receptionist if room is unavailable."
                rows={4}
                className="w-full bg-slate-950/80 border border-slate-700/80 rounded-2xl p-3.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-cyan-400 resize-none font-sans"
              />

              {autoFlowError && (
                <div className="p-3 rounded-xl bg-rose-950/40 border border-rose-500/40 text-xs text-rose-300 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-rose-400 shrink-0" />
                  <span>{autoFlowError}</span>
                </div>
              )}

              {autoFlowSuccess && (
                <div className="p-3 rounded-xl bg-emerald-950/40 border border-emerald-500/40 text-xs text-emerald-300 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
                  <span>{autoFlowSuccess}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-800">
              <button
                type="button"
                onClick={() => setShowAutoFlowModal(false)}
                className="px-4 py-2 rounded-xl text-xs font-bold bg-slate-800 hover:bg-slate-700 text-white transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAutoFlowGenerate}
                disabled={autoFlowGenerating}
                className="px-5 py-2 rounded-xl text-xs font-bold bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-slate-950 flex items-center gap-2 shadow-[0_0_15px_rgba(6,182,212,0.4)] disabled:opacity-50 cursor-pointer"
              >
                {autoFlowGenerating ? (
                  <>
                    <RefreshCw size={13} className="animate-spin" />
                    <span>Architecting Flow...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={13} />
                    <span>Generate AI Flow</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Industry Blueprints Modal */}
      <IndustryBlueprintsModal
        isOpen={showBlueprintsModal}
        onClose={() => setShowBlueprintsModal(false)}
        onSelectBlueprint={handleSelectBlueprint}
      />

      {/* ── Non-Intrusive Pulsing Vertical Cyber Dock Tab for Node Inspector ── */}
      {selectedInspectorNode && !isInspectorExpanded && (() => {
        const liveNode = nodes.find(n => n.id === selectedInspectorNode.id) || selectedInspectorNode;
        const nodeLabel = String((liveNode.data as any)?.agentName || (liveNode.data as any)?.title || (liveNode.data as any)?.customAvatarName || liveNode.type);
        return (
          <div className="fixed right-0 top-1/2 -translate-y-1/2 z-40 animate-in slide-in-from-right duration-200">
            <div className="relative group">
              {/* Sleek Tooltip positioned to the left on hover */}
              <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 pointer-events-none opacity-0 group-hover:opacity-100 transition-all duration-200 translate-x-2 group-hover:translate-x-0 z-50 whitespace-nowrap">
                <div className="px-3 py-2 rounded-xl bg-slate-950/95 border border-cyan-400/40 text-cyan-300 shadow-[0_0_25px_rgba(6,182,212,0.5)] backdrop-blur-md flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5 text-[10px] font-mono font-bold text-cyan-400 uppercase tracking-wider">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-ping inline-block" />
                    Parameters & Logic
                  </div>
                  <div className="text-xs font-bold text-white max-w-[200px] truncate">
                    {nodeLabel}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsInspectorExpanded(true)}
                className="flex flex-col items-center justify-between w-11 h-44 py-3.5 rounded-l-2xl bg-gradient-to-b from-[#0B1528]/98 via-[#060D18]/98 to-[#02050A]/98 border-y-2 border-l-2 border-cyan-400 text-cyan-300 shadow-[0_0_35px_rgba(6,182,212,0.7),inset_0_1px_0_rgba(255,255,255,0.2)] hover:w-12 hover:border-cyan-300 hover:text-white transition-all cursor-pointer active:scale-95"
                title={`Click to expand Parameters & Logic for ${nodeLabel}`}
              >
                {/* Top Chevron with pulsing indicator */}
                <div className="relative flex items-center justify-center">
                  <ChevronLeft size={18} className="text-cyan-400 group-hover:-translate-x-0.5 transition-transform animate-pulse" />
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-cyan-400 animate-ping" />
                </div>

                {/* Center Cyber Sliders Icon Badge */}
                <div className="p-1.5 rounded-lg bg-cyan-500/20 border border-cyan-400/40 text-cyan-300 shadow-[0_0_12px_rgba(6,182,212,0.5)] group-hover:scale-110 transition-transform">
                  <Sliders size={14} className="text-cyan-300" />
                </div>

                {/* Vertical Cyber Label */}
                <div className="flex items-center justify-center [writing-mode:vertical-rl] rotate-180 select-none">
                  <span className="text-[10px] font-black uppercase tracking-[0.22em] text-cyan-200 group-hover:text-white transition-colors">
                    PARAMETERS
                  </span>
                </div>
              </button>
            </div>
          </div>
        );
      })()}

      {/* Node Inspector Studio Slide-Over Drawer */}
      <NodeInspectorStudio
        isOpen={isInspectorExpanded && !!selectedInspectorNode}
        onClose={() => {
          setIsInspectorExpanded(false);
          setSelectedInspectorNode(null);
        }}
        node={(selectedInspectorNode && nodes.find(n => n.id === selectedInspectorNode.id)) || selectedInspectorNode}
        onUpdateNodeData={handleInspectorUpdate}
        isWired={selectedInspectorNode ? isNodeWiredToLunaCore(selectedInspectorNode.id, edges, nodes) : true}
        onAutoWireNode={(nodeId) => {
          const targetNode = nodes.find(n => n.id === nodeId);
          if (targetNode) {
            const coreNode = nodes.find(n => n.type === 'lunaCoreNode' || n.type === 'LUNA_CORE_HUB' || n.id === 'node-luna-core');
            if (coreNode) {
              const configs = getWiringEdgeConfigs(targetNode, coreNode.id);
              if (configs.length > 0) {
                setEdges(prev => {
                  let next = [...prev];
                  for (const cfg of configs) {
                    if (!next.some(e => e.source === cfg.source && e.target === cfg.target && e.sourceHandle === cfg.sourceHandle && e.targetHandle === cfg.targetHandle)) {
                      next.push({
                        id: `edge-${cfg.source}-${cfg.target}-${Date.now().toString(36)}`,
                        source: cfg.source,
                        target: cfg.target,
                        sourceHandle: cfg.sourceHandle,
                        targetHandle: cfg.targetHandle,
                        animated: true,
                        style: { stroke: cfg.color, strokeWidth: 2 }
                      });
                    }
                  }
                  return next;
                });
                showToast(`Wired ${targetNode.id} to Luna Core Hub!`, 'success');
              }
            }
          }
        }}
        openPortalData={onOpenPortalData}
      />
    </div>
  );
};

export const TenetDynamicWorkspace: React.FC<TenetDynamicWorkspaceProps> = (props) => {
  const { pulseState, playSequence, clearPulse } = useSequentialPulse();

  // Legacy: flat activeNodeIds derived from pulseState for backward compat
  const activeNodeIds = useMemo(() => {
    if (!pulseState.isPulsing) return [];
    const ids = new Set(pulseState.visitedNodeIds);
    if (pulseState.currentNodeId) ids.add(pulseState.currentNodeId);
    return Array.from(ids);
  }, [pulseState]);

  const triggerPulse = useCallback((nodeIds: string[]) => {
    // Legacy support: convert flat array to a simple sequential sequence
    const steps: PulseStep[] = nodeIds.map(id => ({ nodeId: id, nodeType: 'unknown' }));
    playSequence(steps, []);
  }, [playSequence]);

  useEffect(() => {
    if (!props.token) return;
    
    // Connect to SSE Endpoint for Real-time Webhook Pulses
    const evtSource = new EventSource(`/api/tenet/sse?token=${encodeURIComponent(props.token)}`);
    
    evtSource.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // Phase 36.5: Sequential execution path from flow resolver
        if (data.type === 'execution_path' && Array.isArray(data.steps)) {
          playSequence(
            data.steps as PulseStep[],
            (data.edges || []) as PulseEdge[]
          );
        }
        // Legacy: simple array-based pulse (backward compatibility)
        else if (data.type === 'node_pulse' && Array.isArray(data.nodeIds)) {
          triggerPulse(data.nodeIds);
        }
        // Clear signal
        else if (data.type === 'pulse_clear') {
          clearPulse();
        }
      } catch (e) {
        console.warn('SSE Parse Error:', e);
      }
    };
    
    evtSource.onerror = (err: any) => {
      // EventSource auto-reconnects automatically on stream resets
      if (evtSource.readyState === EventSource.CLOSED) {
        console.debug('SSE stream closed, retrying...');
      }
    };

    return () => {
      evtSource.close();
    };
  }, [props.token, playSequence, clearPulse, triggerPulse]);

  return (
    <TenantViewContext.Provider value={{ 
      isTenantView: true, 
      authToken: props.token, 
      tenantApiKey: props.tenant?.apiKey,
      tenant: props.tenant,
      isWhiteLabelEnabled: props.tenant?.isWhiteLabelEnabled === true,
      customAvatarUrl: props.tenant?.customAvatarUrl,
      customAvatarName: props.tenant?.customAvatarName,
      pulseState,
      activeNodeIds,
      triggerPulse,
      openPortalData: props.onOpenPortalData,
      openAccountModal: props.onOpenAccountModal
    }}>
      <ReactFlowProvider>
        <FlowWorkspaceInner {...props} />
      </ReactFlowProvider>
    </TenantViewContext.Provider>
  );
};

export default TenetDynamicWorkspace;
